import React from 'react'
import { i18nTranslate, isSubscriptionOverrideCheckoutPage } from 'src/utils'
import { isPaymetric3DSProcessor } from './paymentNameUtils'
import isArray from 'lodash/isArray'
import {
  APPConfig,
  getLocalStorage,
  MYSITE_URL,
  getSessionStorage,
  deleteFromLocalStorage,
  setSessionStorage,
  setCookie,
  getCookie,
  deleteFromSessionStorage,
  enableExpressForRegistered,
  isB2BAccount,
  isB2B2C,
} from 'config/appConfig'
import { localeList } from 'src/App/localeList'
import {
  getLocaleCodeFromUrl,
  getUrlWithoutLocale,
} from 'src/utils/localeUtils'
import { application } from './application'
import { pageNames } from 'src/routes/pathParams'
import { storeContainer } from 'src/models/Store'
import { trackSplunkCustomEvent } from 'src/utils/splunk'
import parse from 'html-react-parser'
import { CONST_PRODUCT_OFFER, PERSONAL_OFFER, PRODUCT_TYPE } from './constant'
// import { isEnableMemberAgreementCheckout, isEnableMemberAgreementsChange } from 'src/views/components/Header/deps'

const IS_BROWSER = typeof window === 'object'
const DEFAULT_AFFILIATION = 'nuskin'
const DEFAULT_BRAND = 'Nu Skin'

/**
 * Converts a value to a boolean.
 *
 * @param {*} booleanValue - The value to convert.
 * @returns {boolean} The converted boolean value.
 */
function convertToBoolean(booleanValue) {
  try {
    if (booleanValue) {
      return !!JSON.parse(String(booleanValue).toLowerCase())
    }
  } catch (e) {
    return false
  }
  return false
}
function enableNewFeature(param) {
  const result = convertToBoolean(APPConfig?.getAppConfig()?.[param])
  return result
}

/**
 * Determines whether PV/CV/SV values should be displayed for the current account.
 *
 * Brand Affiliate accounts are eligible unless disabled via the `disablePVCVSVPrice`
 * flag (this gating can be turned off with `gateBrandAffiliate: false` for surfaces
 * where Brand Affiliate values are always shown). Preferred Customer/Member accounts
 * are eligible unless disabled via the `disablePVCVSVPriceForMember` flag.
 *
 * @param {Object} [options] - Options.
 * @param {string} [options.accountType] - Account type string. Defaults to the stored `accountType`.
 * @param {boolean} [options.gateBrandAffiliate=true] - When false, Brand Affiliate is always eligible (not gated by `disablePVCVSVPrice`).
 * @returns {boolean} True when PV/CV/SV values should be shown.
 */
const isEligibleToShowPVCVSV = ({
  accountType = getLocalStorage('accountType') || '',
  gateBrandAffiliate = true,
} = {}) => {
  const appConfig = APPConfig?.getAppConfig() || {}
  const isDisablePVCVSVPrice = convertToBoolean(appConfig?.disablePVCVSVPrice)
  const isDisablePVCVSVPriceForMember = convertToBoolean(
    appConfig?.disablePVCVSVPriceForMember
  )
  const isBrandAffiliate = accountType?.includes('Brand Affiliate')
  const isMember = accountType?.includes('Preferred Customer/Member')
  return (
    (isBrandAffiliate && (!gateBrandAffiliate || !isDisablePVCVSVPrice)) ||
    (isMember && !isDisablePVCVSVPriceForMember)
  )
}

/**
 * Resolves the account-aware "disable PV/CV/SV price" flag.
 * Member accounts use `disablePVCVSVPriceForMember`; all others use `disablePVCVSVPrice`.
 *
 * @param {Object} [options] - Options.
 * @param {string} [options.accountType] - Account type string. Defaults to the stored `accountType`.
 * @returns {boolean} True when PV/CV/SV price should be disabled for the account.
 */
const getIsDisablePVCVSVPriceForAccount = ({
  accountType = getLocalStorage('accountType') || '',
} = {}) => {
  const appConfig = APPConfig?.getAppConfig() || {}
  const isMember = accountType?.includes('Preferred Customer/Member')
  return convertToBoolean(
    isMember
      ? appConfig?.disablePVCVSVPriceForMember
      : appConfig?.disablePVCVSVPrice
  )
}

/**
 * Observes an element using IntersectionObserver API.
 * Sets up observer with given options and callback.
 * Starts observing the element matching the selector.
 */
function observeElement(element, callback) {
  let observer = []
  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 0.01,
  }
  const startObserve = () => {
    observer = new IntersectionObserver(callback, options)
    const target = document.querySelector(element)
    observer.observe(target)
  }

  startObserve()
}

/**
 * Gets the currency code for the given locale.
 *
 * Checks localStorage for a saved currency code.
 * Falls back to looking up the locale in the locale list to get the currency code.
 * Finally defaults to 'USD' if no currency code is found.
 */
function getCurrency(locale) {
  // const selectedLocale = storeContainer.selectedLocaleList
  //const localeArray = selectedLocale.length ? selectedLocale : localeList()
  let currency = getLocalStorage('currency')
  if (currency) {
    return currency
  }

  /**
   * @todo
   * !!! local list should be dynamic
   * !!! NOT from fixtures
   */
  const localeArray = localeList()
  const currentLocale = localeArray.find(
    localeValue => localeValue.value === locale
  )

  // For LATAM Markets - Currency Code is taken from Active Store API Response - defaultCurrency Node
  let currencyCodeFromStoreResponse =
    storeContainer?.activeStoreResponse?.defaultCurrency || ''

  if (getLocalStorage('currency')) {
    return getLocalStorage('currency')
  } else if (currentLocale && currentLocale.currency) {
    return currentLocale.currency
  } else if (currencyCodeFromStoreResponse) {
    return currencyCodeFromStoreResponse
  }
  return 'USD'
}

/**
 * Formats a price value with a currency code and locale.
 *
 * Takes in a price and currency code.
 * Gets the locale from the default app locale.
 * Formats the price into a localized currency string using Intl.NumberFormat.
 * Falls back to a default currency code if formatting fails.
 */
function currencyFormat(props) {
  const {
    currency,
    price,
    currencyWithoutDecimal = false,
    isPVCV = false,
  } = props
  const enableVolumeFormatting = isFeatureEnabled('enableVolumeFormatting')
  if (isPVCV && enableVolumeFormatting) {
    return volumeFormat({
      value: Number.parseFloat(price) || 0,
    })
  }
  let finalPrice = price
  const { defaultLocale = '', normalizeCurrencyLocale = {} } =
    APPConfig.getAppConfig() || {}
  let originalLocale = defaultLocale
    .replace('_', '-')
    ?.replace(/[^a-zA-Z0-9-]/, '')
  let updatedLocale = originalLocale
  const localeCountry = updatedLocale.split('-')[1]
  if (updatedLocale.includes('-US')) {
    updatedLocale = 'en-US'
    // CX121-2874
  } else if (updatedLocale.includes('-CA')) {
    updatedLocale = 'en-CA'
    //CX121-4864
  } else if (
    localeCountry &&
    normalizeCurrencyLocale[localeCountry.toLowerCase()]
  ) {
    const languageCode = normalizeCurrencyLocale[localeCountry.toLowerCase()]
    updatedLocale = languageCode + '-' + localeCountry
  }
  const currencyCode = currency ? currency : getCurrency(updatedLocale)
  const displayNarrowSymbol =
    enableNewFeature('enableNarrowSymbolCurrencyFormat') || false
  const enableDecimalRoundingInPrice =
    enableNewFeature('enableDecimalRoundingInPrice') || false
  const excludeSpaceInPrice = enableNewFeature('excludeSpaceInPrice') || false

  const priceFormat = currencyCode => {
    const formatOptions = {
      style: 'currency',
      currency: currencyCode,
    }
    if (enableDecimalRoundingInPrice && !currencyWithoutDecimal) {
      formatOptions.minimumFractionDigits = 2
      formatOptions.maximumFractionDigits = 2
      finalPrice = Math.round(finalPrice)
    }
    if (currencyWithoutDecimal) {
      formatOptions.maximumFractionDigits = 0
      formatOptions.minimumFractionDigits = 0
    }
    if (displayNarrowSymbol) {
      formatOptions.currencyDisplay = 'narrowSymbol'
    }
    const formatted = new Intl.NumberFormat(
      updatedLocale,
      formatOptions
    ).format(finalPrice)
    return excludeSpaceInPrice
      ? formatted.replace(/[ \u00A0\u2009]/g, '')
      : formatted
  }

  /**
   * @todo
   * !!! code shorting required
   * !!! as its closely duplicate code
   */
  try {
    const isPOAdmin =
      IS_BROWSER &&
      window?.location?.pathname?.includes(pageNames.productOfferAdmin) &&
      enableNewFeature('enableProductOfferV2')
    if (isPOAdmin) {
      const poLocale = getSessionStorage('POAdminSelectedMarketLocale')
      if (poLocale) {
        updatedLocale = poLocale
      }
    }
    let finalValue = priceFormat(currencyCode)
    return finalValue
  } catch (e) {
    console.warn('wrong currency code')
    const staticCurrency = getCurrency(updatedLocale)
    const finalValue = priceFormat(staticCurrency)
    return finalValue
  }
}

/**
 * Formats a numeric value with fixed decimal places without currency symbols.
 * Designed for volume/point values like PV (Personal Volume), CV (Commissionable Volume),
 * and SV (Sales Volume) that should NOT follow currency-specific rounding rules.
 *
 * Unlike currencyFormat, this uses 'decimal' style instead of 'currency' style,
 * which prevents currency-specific rounding (e.g., JPY rounding to whole numbers).
 *
 * @param {Object} props - Configuration object
 * @param {number} props.value - The numeric value to format
 * @param {number} [props.minimumFractionDigits=2] - Minimum decimal places (default: 2)
 * @param {number} [props.maximumFractionDigits=2] - Maximum decimal places (default: 2)
 * @param {string} [props.locale] - Optional locale override (e.g., 'en-US', 'ja-JP')
 * @returns {string} Formatted decimal string (e.g., "123.67", "123.00")
 *
 * @example
 * // Basic usage
 * volumeFormat({ value: 123.67 })  // "123.67"
 * volumeFormat({ value: 123 })     // "123.00"
 *
 * @example
 * // Japanese locale (no currency rounding)
 * volumeFormat({ value: 123.67, locale: 'ja-JP' })  // "123.67" (NOT "124")
 *
 */
function volumeFormat(props) {
  const {
    value,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale = null,
  } = props

  const { defaultLocale = '', normalizeCurrencyLocale = {} } =
    APPConfig.getAppConfig() || {}
  let updatedLocale =
    locale || defaultLocale.replace('_', '-')?.replace(/[^a-zA-Z0-9-]/g, '')

  const localeCountry = updatedLocale.split('-')[1]
  if (updatedLocale.includes('-US')) {
    updatedLocale = 'en-US'
  } else if (updatedLocale.includes('-CA')) {
    updatedLocale = 'en-CA'
  } else if (
    localeCountry &&
    normalizeCurrencyLocale[localeCountry.toLowerCase()]
  ) {
    const languageCode = normalizeCurrencyLocale[localeCountry.toLowerCase()]
    updatedLocale = languageCode + '-' + localeCountry
  }

  try {
    const formatted = new Intl.NumberFormat(updatedLocale, {
      style: 'decimal', // NOT 'currency' - avoids currency-specific rounding
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(value)

    // return 'new format demo: ' + formatted
    return formatted
  } catch (e) {
    console.warn('volumeFormat: fallback to toFixed', e)
    return Number(value || 0).toFixed(maximumFractionDigits)
  }
}

/**
 * Returns currency symbol only for current locale/currency.
 * Example: '$', '€', 'kr'
 */
function getCurrencySymbol(currency = '') {
  try {
    const formattedPrice = String(
      currencyFormat({
        price: 0,
        currency,
        currencyWithoutDecimal: true,
      }) || ''
    )

    const symbolOnly = formattedPrice
      .replace(/[0-9.,\-()]/g, '')
      .replace(/[ \u00A0\u2009]/g, '')
      .trim()

    return symbolOnly || formattedPrice
  } catch (e) {
    const fallbackCurrency = getCurrency('en-US')
    return fallbackCurrency || 'USD'
  }
}

/**
 * Deformats a currency-formatted string back to a numeric value.
 * Intelligently detects whether dots/commas are thousand or decimal separators.
 *
 * Handles both US format ("1,234.56") and European format ("1.234,56").
 * Returns a numeric string with dot as decimal separator.
 *
 * @param {string} formattedValue - The formatted currency string (e.g., "1,234.56" or "1.234,56")
 * @returns {string} Numeric string with dot as decimal (e.g., "1234.56")
 */
function deformatCurrency(formattedValue = '') {
  if (!formattedValue) return ''

  // Remove currency symbols and non-digit/separator characters
  let clean = String(formattedValue).replace(/[^\d.,]/g, '')

  // PARSING: Handle autofill/paste with trailing punctuation"
  let lastNumericCharIndex = clean.length - 1
  while (
    lastNumericCharIndex >= 0 &&
    (clean[lastNumericCharIndex] === '.' || clean[lastNumericCharIndex] === ',')
  ) {
    lastNumericCharIndex -= 1
  }
  clean = clean.slice(0, lastNumericCharIndex + 1)

  if (!clean) return ''

  // Detect format: find last occurrence of . or ,
  const lastDotIndex = clean.lastIndexOf('.')
  const lastCommaIndex = clean.lastIndexOf(',')
  // Determine which is the decimal separator
  let decimalSeparator = '.'
  let thousandSeparator = ','

  if (lastCommaIndex > lastDotIndex) {
    // European format: "1.234,56" - comma is decimal
    decimalSeparator = ','
    thousandSeparator = '.'
  }

  // Remove thousand separators and replace decimal separator with dot
  return clean
    .replace(new RegExp('\\' + thousandSeparator, 'g'), '')
    .replace(decimalSeparator, '.')
}

/**
 * Returns the name of the attribute element with the given propName
 * from the given propList array.
 *
 * @param {Object[]} propList - The array of attribute elements
 * @param {string} propName - The name of the attribute to find
 * @returns {string} The name of the matching attribute element
 */
function getProductPDPTitle(propList, propName) {
  return (
    propList?.find(element => element?.attributeId?.toLowerCase() == propName)
      ?.name || ''
  )
}

/**
 * Checks if a video URL is a YouTube video URL.
 *
 * Takes in a video URL string.
 * Uses a regular expression to match known YouTube URL patterns.
 * Extracts the video ID if a match is found.
 * Returns true if the URL matches a YouTube pattern, false otherwise.
 */
function isYouTubeVideo(videoUrl = '') {
  if (videoUrl != '') {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = videoUrl && videoUrl.match(regExp)
    let videoId = match?.[2].length === 11 ? match[2] : null
    if (videoUrl && videoUrl.match(regExp)) {
      return true
    }
  }
  return false
}

/**
 * Renders appropriate media component based on the mediaProps passed in.
 *
 * Checks if url is a YouTube video, and renders iframe if so.
 * Checks if url matches video regex, and renders video tag if so.
 * Checks if url matches image regex, and renders img tag if so.
 * Provides default values for mediaProps.
 * Returns empty fragment if no match.
 */
function getMedia(mediaProps) {
  const {
    url = '',
    youTubeVideoId = '',
    alt = '',
    index = 0,
    isToShowControls = true,
    thumbnail = '',
  } = mediaProps
  const videoUrlRegEx = /^https?:\/\/.*\/.*\.(mp4|webm|ogg)\??.*$/gim
  const imageUrlRegEx = /\.(gif|jpe?g|tiff?|png|webp|bmp)($|\?)/i

  // If youTubeVideoId is provided, use it directly
  if (youTubeVideoId) {
    return (
      <iframe
        tabIndex={index}
        width="100%"
        className="media"
        src={`https://www.youtube.com/embed/${youTubeVideoId}?controls=${isToShowControls ? '0' : '1'}`}
        title={alt}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    )
  }

  if (isYouTubeVideo(url)) {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = url.match(regExp)
    const videoId = match && match[2]?.length === 11 ? match[2] : null
    if (videoId) {
      return (
        <iframe
          tabIndex={index}
          width="100%"
          className="media"
          src={`https://www.youtube.com/embed/${videoId}?controls=${isToShowControls ? '0' : '1'}`}
          title={alt}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )
    }
  }

  if (url.match(videoUrlRegEx)) {
    return (
      <video
        tabIndex={index}
        className="sustainability-video"
        width="100%"
        controls={isToShowControls}
        poster={thumbnail}>
        <source src={url} />
      </video>
    )
  }
  if (url.match(imageUrlRegEx)) {
    return <img tabIndex={index} width="100%" src={url} alt={alt || ''} />
  }
  return <></>
}

/**
 * Returns localized strings for SKU attributes.
 *
 * Uses i18n library to look up translations for common SKU attribute names like 'Size',
 * 'Color', etc. Provides fallback default strings if translation is missing.
 *
 * Intended for use in rendering SKU selector components to show localized labels.
 */
function getLocaleString() {
  return {
    size: i18nTranslate('sku.size', 'Size'),
    color: i18nTranslate('sku.color', 'Color'),
    fit: i18nTranslate('sku.fit', 'Fit'),
    fittype: i18nTranslate('sku.fit', 'Fit'),
    skintype: i18nTranslate('sku.skintype', 'Select Skin Type'),
    variantLabel: i18nTranslate('sku.variantLabel', 'Variant'),
  }
}

/**
 * Validates if the given string is a valid URL.
 *
 * @param {string} string - The string to validate.
 * @returns {boolean} True if the string is a valid URL, false otherwise.
 */
function isValidUrl(string) {
  try {
    new URL(string)
    return true
  } catch (e) {
    return false
  }
}

/**
 * Returns responsive value based on application viewport size.
 *
 * Checks `application.isDesktopOrLarger`, `application.isTabletOrLarger`
 * to determine which value to return for desktop, tablet or mobile.
 *
 * @param {*} desktop - Value for desktop viewport.
 * @param {*} tablet - Value for tablet viewport.
 * @param {*} mobile - Value for mobile viewport.
 * @returns {*} Responsive value for current viewport.
 */
function setResponsiveValues(desktop, tablet, mobile) {
  return application.isDesktopOrLarger
    ? desktop
    : application.isTabletOrLarger
      ? tablet
      : mobile
}

/**
 * Tries to parse a JSON string and return the parsed value.
 * If parsing fails, returns the provided default value if defined,
 * otherwise returns the original data.
 *
 * @param {string} data - The JSON string to parse
 * @param {*} [defaultValue] - The default value to return on failure
 * @returns {*} The parsed JSON value or the default value
 */
function tryJSONParse(data, defaultValue) {
  try {
    const value = JSON.parse(data)
    return value
  } catch (e) {
    return defaultValue !== undefined ? defaultValue : data
  }
}

/**
 * Tries to parse a value as an integer, returning a default value on failure.
 *
 * @param {*} data - The data to parse
 * @param {*} defaultValue - The value to return on failure
 * @returns The parsed integer, or the default value on failure
 */
function safeParseInt(data, defaultValue) {
  try {
    const value = parseInt(data)
    return !isNaN(value)
      ? value
      : defaultValue !== undefined
        ? defaultValue
        : data
  } catch (e) {
    return defaultValue !== undefined ? defaultValue : data
  }
}

/**
 * Tries to decode a URI encoded string, catching and ignoring any errors.
 *
 * @param {string} text - The URI encoded string to decode
 * @returns {string} The decoded string, or the original string if decoding failed
 */
function tryDecode(text) {
  try {
    const value = decodeURI(text)
    return value
  } catch (e) {
    return text
  }
}

function toAmount(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Number(value.toFixed(2)) : 0
  }
  if (typeof value === 'string') {
    const normalized = value.replaceAll(/[^0-9.-]+/g, '')
    const parsed = Number.parseFloat(normalized)
    return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0
  }
  return 0
}

function toQuantity(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const SUBSCRIPTION_FREQUENCY_LABELS = [
  { keyword: 'month', label: 'monthly', periodLabel: 'months' },
  { keyword: 'week', label: 'weekly', periodLabel: 'weeks' },
  { keyword: 'year', label: 'yearly', periodLabel: 'years' },
  { keyword: 'day', label: 'daily', periodLabel: 'days' },
]

function getFrequencyLabel(subscription) {
  if (!subscription) {
    return 'NA'
  }
  const { frequencyType, frequencyPeriod } = subscription
  if (!frequencyType && !frequencyPeriod) {
    return 'NA'
  }
  const normalizedType = `${frequencyType || ''}`.toLowerCase()
  const periodNumber = Number(frequencyPeriod)
  const hasValidPeriod = Number.isFinite(periodNumber) && periodNumber > 0
  const frequencyMatch = SUBSCRIPTION_FREQUENCY_LABELS.find(({ keyword }) =>
    normalizedType.includes(keyword)
  )
  if (frequencyMatch) {
    if (hasValidPeriod && periodNumber !== 1) {
      const periodLabel =
        frequencyMatch.periodLabel || `${frequencyMatch.keyword}s`
      return `${periodNumber} ${periodLabel}`.trim()
    }
    return frequencyMatch.label
  }

  if (frequencyPeriod) {
    return `${frequencyPeriod}-${normalizedType}`.trim()
  }
  return normalizedType
}

function getItemVariant(sku = {}) {
  const skuType = `${sku?.type || ''}`.toLowerCase()
  if (skuType === 'bundle' || skuType === 'skukit' || skuType === 'kit') {
    return 'bundle-kit'
  }
  return (
    sku?.skuProperties?.variantLabel ||
    sku?.skuProperties?.type?.toLowerCase() ||
    'NA'
  )
}

function getItemCouponCode(item = {}) {
  const itemCodes = item?.promotionDetails?.codes
  if (Array.isArray(itemCodes) && itemCodes[0]) {
    const firstCode = itemCodes[0]
    if (typeof firstCode === 'string') {
      return firstCode
    }
    return (
      firstCode?.code || firstCode?.couponCode || firstCode?.promotionCode || ''
    )
  }
  const promotions = item?.promotionDetails?.promotions
  if (Array.isArray(promotions)) {
    const promo = promotions.find(
      promotion =>
        promotion?.code ||
        promotion?.couponCode ||
        promotion?.promotionCode ||
        promotion?.breakup?.[0]?.displayMessage
    )
    let coupon = []
    if (promo?.breakup && isArray(promo?.breakup)) {
      coupon = promo?.breakup
        ?.flatMap(
          promotion =>
            promotion?.code || promotion?.couponCode || promotion?.promotionCode
        )
        ?.filter(promo => promo)
        ?.join(', ')
    }
    return (
      promo?.code || promo?.couponCode || promo?.promotionCode || coupon || 'NA'
    )
  }
  return 'NA'
}

/**
 * Resolves the brand name from product/sku properties with a fallback.
 * Checks multiple property keys in order of priority.
 *
 * @param {Object} options - Options object
 * @param {Object} options.skuProps - SKU level properties object
 * @param {Object} options.productProps - Product level properties object
 * @param {string} options.fallback - Fallback value if no brand found
 * @returns {string} The resolved brand name
 */
function resolveItemBrand(options = {}) {
  const { skuProps = {}, productProps = {}, fallback = DEFAULT_BRAND } = options
  return (
    skuProps?.brand ??
    skuProps?.brandName ??
    skuProps?.productBrand ??
    productProps?.brand ??
    productProps?.brandName ??
    fallback
  )
}

/**
 * Encodes the given search term by URI encoding it and escaping any % and . characters.
 * This allows the term to be safely used in a URL search query.
 * @note
 * React router search encode related containing characters % and .
 *
 * @param {string} search - The search term to encode
 * @returns {string} The encoded search term
 */
function encodeSearchTerm(search) {
  return encodeURI(search.replace(/%/g, '%25').replace(/\./g, '%252E'))
}

/**
 * Decodes a search term that was encoded for use in a URL search query.
 *
 * Replaces any %2E sequences with . characters.
 * @note
 * React router search encode related containing characters % and .
 *
 * @param {string} search - The encoded search term
 * @returns {string} The decoded search term
 */
function decodeSearchTerm(search) {
  return tryDecode(search).replace(/%2E/g, '.')
}

/**
 * Gets the locale path parameter from the default locale in the active app config.
 * Splits the locale on '_', reverses it, joins with '/' and converts to lowercase.
 * @note
 * Extracting locale from config and returning it in reversed path params format. .
 *
 * @returns {string} The locale path parameter
 */
function getLocalePathParam() {
  const locale = APPConfig.getActiveAppConfig().defaultLocale
  return locale?.split('_')?.reverse()?.join('/')?.toLowerCase()
}

/**
 * Replaces the locale placeholder in a URL with the actual locale code.
 *
 * Checks if the given URL contains the '<locale>' placeholder.
 * If so, determines the current locale code and replaces the placeholder with it.
 *
 * Falls back to 'en_US' if no locale can be determined.
 * Special cases 'zh_CN' to 'zh_CA'.
 *
 * @param {Object} options - The options object
 * @param {string} options.url - The URL to replace the locale in
 * @returns {string} The URL with the locale placeholder replaced
 */
function replaceLocaleInURL(options) {
  let { url } = options
  const localeTemplate = '<locale>'
  if (url?.includes(localeTemplate)) {
    let currentLocale = getLocalePathParam()
    let locale
    currentLocale = currentLocale && currentLocale?.split('/')
    if (isArray(currentLocale) && currentLocale?.length > 0) {
      locale = `${currentLocale?.[1]}_${currentLocale?.[0]?.toUpperCase()}`
    } else {
      // fallback to en_US for safety
      locale = 'en_US'
    }
    if (locale === 'zh_CN') {
      locale = 'zh_CA'
    }
    return url?.replace(localeTemplate, locale)
  }
  return url
}

// Replace this function with server config file
/**
 * Checks if a given link matches any of the internal page URLs.
 *
 * @param {Object} options - Options object
 * @param {string} options.link - Link to check
 * @param {string} options.pageUrl - Current page URL
 * @returns {Object}
 * @returns {boolean} return.isInternalPage - Whether link is an internal page
 * @returns {string} return.urlWithoutLocale - Link with locale removed
 */
function isPageFound(options) {
  const appConfigFromJSON = APPConfig.getConfigFromJSON()
  const { link = '', pageUrl = '' } = options || {}

  const currentPageUrl = (IS_BROWSER ? location.href : pageUrl) || ''

  const curLocaleFromUrl = getLocaleCodeFromUrl({
    url: currentPageUrl,
    at: 'pathParam',
    defaultLocale: appConfigFromJSON?.defaultLocale,
    isReverseType: true,
  })
  const localeInUrl = curLocaleFromUrl
    ?.split('_')
    ?.reverse()
    ?.join('/')
    ?.toLowerCase()

  let urlWithoutLocale = link?.replace(`/${localeInUrl}`, '')

  urlWithoutLocale = urlWithoutLocale ? urlWithoutLocale : '/'
  const urlWithoutQueryParam = urlWithoutLocale?.split('?')[0]?.split('#')[0]

  const pageUrls = Object.values(pageNames)
  let isInternalPage = false

  if (
    urlWithoutQueryParam?.includes('https://') ||
    urlWithoutQueryParam?.includes('http://')
  ) {
    isInternalPage = false
  } else {
    isInternalPage = pageUrls.some(pageUrl => {
      if (pageUrl == urlWithoutQueryParam) {
        return true
      }
      if (pageUrl != '/') {
        return urlWithoutQueryParam.includes(`${pageUrl}/`)
      }
    })
  }

  return {
    isInternalPage,
    urlWithoutLocale,
  }
}

/**
 * Checks if a given link matches any internal page URL and returns
 * formatted link details.
 *
 * Removes locale from link if present. Checks if resulting link matches
 * any internal page URL without locale.
 *
 * Returns object with formatted link and boolean indicating if link is
 * internal or third party.
 */
function renderLinkCheck(href = '', originalUrl) {
  let storeFrontRoutes = Object?.values(pageNames) || []
  const index = storeFrontRoutes?.indexOf('/')
  storeFrontRoutes?.splice(index, 1)
  const activeEnvironment = APPConfig?.getLocalEnv() || ''

  const curLocale = APPConfig?.getDefaultProps?.()?.defaultLocale || 'en_US'
  const [language, activeCountry] = curLocale.includes('_')
    ? curLocale.split('_')
    : curLocale.split('-')

  // Removed this since we are not going use *.skavcommerce domain anymore
  // const baseurl = 'https://www.nuskin.com'
  let hreftext = ''

  let pageDetails
  try {
    pageDetails = isPageFound({ link: href, pageUrl: originalUrl })
  } catch (e) {
    console.log('isInternalPage ', e)
  }

  let isThirdPartyPage = true
  if (pageDetails?.isInternalPage) {
    hreftext = pageDetails?.urlWithoutLocale || ''
    isThirdPartyPage = false
  } else {
    hreftext = href
  }

  return { link: hreftext, isThirdPartyPage }
}

/**
 * Gets the URL for the MySite shopping cart page.
 *
 * Checks if the user is in a MySite or Personal Offer context via `checkMysiteOrPersonalOffer()`.
 *
 * For MySite, gets the MySite domain from sessionStorage and builds the cart URL using the MYSITE_REDIRECT config value.
 *
 * For Personal Offer, gets the user's landingPageURL from sessionStorage.
 *
 * Returns the cart URL string for either MySite or Personal Offer based on context.
 */
function getMysiteCartUrl() {
  // const shoppingContext = getLocalStorage('shoppingContext') || {}
  const contextValue = checkMysiteOrPersonalOffer()
  if (IS_BROWSER) {
    // const activeLocale = getLocalStorage('locale')
    const activeLocale = getLocaleCodeFromUrl({
      defaultLocale: 'en_US',
      isReverseType: true,
    })
    if (contextValue == 'personal_offer') {
      let userDetails = sessionStorage.getItem('personalOffer') || {}
      if (userDetails) {
        userDetails = JSON.parse(userDetails)
      }
      if (userDetails?.shareLinks) {
        const market = activeLocale?.split('_')?.[1] || ''
        return userDetails?.shareLinks?.[market] || ''
      }
      return userDetails?.landingPageURL || ''
    }
    if (contextValue == 'storefront') {
      let mySiteDomain
      let urlDetails = sessionStorage.getItem('storefront') || {}
      if (urlDetails) {
        urlDetails = JSON.parse(urlDetails)
      }
      if (Object.keys(urlDetails?.storefront).length > 0) {
        mySiteDomain = Object.keys(urlDetails?.storefront)
        mySiteDomain = mySiteDomain?.[0] || ''
      }
      const MYSITE_LOGO_REDIRECT =
        APPConfig?.getAppConfig()?.MYSITE_REDIRECT || ''
      return MYSITE_LOGO_REDIRECT.replace('<%domain%>', mySiteDomain)
    }
  }
}

/**
 * Returns the MySite URL with the active locale replaced.
 *
 * Checks if a custom client domain is configured and uses that if available,
 * otherwise falls back to the default MYSITE_URL.
 *
 * Replaces the <%LOCALE%> placeholder in the URL with the locale code
 * from the current URL.
 *
 * Used to generate MySite URLs dynamically with the locale needed for the current user.
 */
function getMySiteUrl() {
  if (IS_BROWSER) {
    // const activeLocale = getLocalStorage('locale')
    const activeLocale = getLocaleCodeFromUrl({
      defaultLocale: 'en_US',
      isReverseType: true,
    })
    const { clientDomainName = '' } = APPConfig?.getAppConfig() || {}
    const updatedMySiteUrl =
      clientDomainName !== ''
        ? `https://${clientDomainName}` + MYSITE_URL
        : MYSITE_URL
    return updatedMySiteUrl?.replace('<%LOCALE%>', activeLocale) || ''
  }
}

/**
 * Checks if a URL is absolute based on the presence of a protocol or leading slashes.
 *
 * @param {string} url - The URL to check.
 * @returns {boolean} True if the URL is absolute, false otherwise.
 */
function isUrlAbsolute(url) {
  return url.indexOf('://') > 0 || url.indexOf('//') === 0
}

/**
 * Retrieves the IP address of the browser.
 *
 * Tries to make a request to get the IP address based on the current domain.
 * Falls back to non-CORS request if SSR is disabled.
 *
 * Returns the IP address string if successful, empty string if not.
 */
async function getBrowserIpAddress() {
  // In local we are disabling CORS so enabling proxy only for PDN
  let ipAddress = ''
  try {
    ipAddress = await fetch('https://api.ipify.org/?format=json')
    ipAddress = await ipAddress.json()
  } catch (err) {
    console.error('getBrowserIpAddress in commonUtils --->', err)
  }
  return ipAddress?.ip || ''
}

//adding showWholeSalePricing property in cart , order review , order confirmation request header for mysite
/**
 * Retrieves the wholesale pricing flag for the current shopping context.
 *
 * Checks the shoppingContext parameter to see if it is 'storefront' or 'personal_offer'.
 *
 * For 'storefront', gets the storefront data from sessionStorage, extracts the current locale,
 * and looks up the wholesale flag from the storefront config for that locale.
 *
 * For 'personal_offer', simply returns the showWholeSalePricing override directly.
 *
 * Returns the wholesale pricing flag value if found, empty string if not.
 */
function getMySitePriceFlag(shoppingContext) {
  try {
    let contextValue = shoppingContext?.context
    let wholesale
    if (contextValue === 'storefront') {
      const storefront = getSessionStorage('storefront') || {}
      const curLocale = getLocaleCodeFromUrl() || {}
      const [language, country] = curLocale.includes('_')
        ? curLocale.split('_')
        : curLocale.split('-')
      wholesale =
        JSON.stringify(
          storefront?.['storefront']?.['mytestsqa']?.[`${country}`]?.['lang']?.[
            `${language}`
          ]?.['wholesale']
        ) || ''
    }

    if (contextValue === 'personal_offer') {
      wholesale = shoppingContext?.overrides?.showWholeSalePricing
    }
    return wholesale
  } catch (error) {
    console.log(
      'error while getting wholesale price config - Mysite & Personal Offer'
    )
  }
}

/**
 * Checks if a button should be enabled based on a list of available channels
 * and a specific channel value.
 *
 * @param {string[]} availableChannelsList - Array of available channel values
 * @param {string} value - The channel value to check
 * @returns {boolean} True if button should be enabled, false otherwise
 */
function checkEnableButton(availableChannelsList, value) {
  if (availableChannelsList != '' && availableChannelsList?.includes(value)) {
    return true
  } else if (
    availableChannelsList != '' &&
    !availableChannelsList?.includes(value)
  ) {
    return false
  } else if (availableChannelsList == '') {
    return true
  }
}

/**
 * Gets the two-letter country code from the provided countryCode option.
 *
 * If countryCode is 'canada' or 'ca', returns 'CA'.
 * Otherwise returns the countryCode unchanged.
 *
 * May need additional cases for converting country names to codes in the future.
 */
function getCountryCode(options) {
  const { countryCode } = options
  if (
    countryCode?.toLowerCase() == 'canada' ||
    countryCode?.toLowerCase() == 'ca'
  ) {
    return 'CA'
  }
  // may need for US to USA in future or any other country
  return countryCode
}

/**
 * Gets the image URL from the given product properties.
 *
 * Checks primaryimage and imageURL first.
 * If not found, tries to parse image URL from productImages array.
 *
 * @param {Object} properties - Product properties object
 * @returns {string} Image URL string
 */
function getImageFromProperty(properties = {}) {
  let image = properties?.primaryimage || properties?.imageURL || ''
  if (!image) {
    try {
      image = JSON.parse(properties?.productImages || '{}')?.[0]?.url || ''
    } catch (e) {
      console.log('image parse error')
    }
  }
  return image
}

/**
 * To identify the current URL shares with my site or personal offer
 * @date 2023/8/17 - 14:24:35
 *
 * @returns boolean - true if the URL matches the current URL, false if not matches
 */
function checkIsMySiteUrl() {
  if (IS_BROWSER) {
    const isMySiteUrls = [
      pageNames.home,
      pageNames.loginCallback,
      pageNames.viewCart,
      pageNames.signUp,
      pageNames.checkout,
      pageNames.orderConfirmation,
      pageNames.myaccountsubscriptions,
      pageNames.expressCheckout,
      pageNames.klarnaCheckout,
      pageNames.equinoxPersonalOffer,
      pageNames.myaccountwallet,
    ]

    const isMySiteUrl = isMySiteUrls.some(url => {
      if (location?.href?.includes(url)) {
        return true
      }
    })
    return isMySiteUrl
  }
}

/**
 * This function is to get current site is storefront or personal offer or my site
 *
 * @date 2023/8/17 - 13:57:08
 */
function checkMysiteOrPersonalOffer() {
  let contextValue = ''
  try {
    if (IS_BROWSER) {
      let isMySiteUrl = checkIsMySiteUrl()
      const shoppingContext = getShoppingContext()
      // const shoppingContext = getLocalStorage('shoppingContext') || {}
      if (Object.keys(shoppingContext).length > 0 && isMySiteUrl) {
        contextValue = shoppingContext?.context || ''
      }
      // Fallback: detect PO context from URL when shoppingContext is missing
      if (!contextValue && isRedesignPOUrl()) {
        contextValue = 'personal_offer'
      }
    }
    return contextValue
  } catch (e) {
    console.error('Fn error in checkMysiteOrPersonalOffer', e)
  }
}

//
/**
 * Shopping context value is used read from window object instead of local storage
 * @date 2023/8/17 - 16:44:19
 * @returns { object }
 */
function getShoppingContext() {
  let shoppingContext = {}
  if (IS_BROWSER) {
    const isNativeMobileApp =
      window?.isNativeApp == true || window?.isVeraApp == true

    const searchParams = location?.search || ''
    let isNativeAppQueryParamString = new URLSearchParams(searchParams)?.get(
      'isNativeApp'
    )

    if (isNativeMobileApp) {
      shoppingContext =
        isRedesignMysiteUrl() || isRedesignPOUrl()
          ? getSessionStorage('shoppingContext')
          : getLocalStorage('shoppingContext') || {}
    } else {
      // `window.shoppingContextJson` is in-memory only, so it is lost on any
      // full page load where `setMysiteOrPersonalOffer()` doesn't run (PO/mysite
      // redesign persist the same context in sessionStorage).
      shoppingContext =
        window?.shoppingContextJson ||
        (isRedesignMysiteUrl() || isRedesignPOUrl()
          ? getSessionStorage('shoppingContext')
          : null) ||
        getShoppingContextPrysm() ||
        {}
    }
  }
  // const shoppingContext = getLocalStorage('shoppingContext') || {}
  return shoppingContext
}

function getShoppingContextPrysm() {
  const shoppingContext =
    getSessionStorage('shoppingContext') ||
    getLocalStorage('shoppingContext') ||
    {}
  if (shoppingContext?.context?.toLowerCase() === 'mobile_prysm') {
    return shoppingContext
  }
  return {}
}

/**
 * Clears the shopping context from local storage and window object.
 *
 * @date 2023/8/17
 */
function clearShoppingContext() {
  try {
    if (IS_BROWSER) {
      deleteFromLocalStorage('shoppingContext')
      window.shoppingContextJson = {}
    }
  } catch (e) {
    console.log(
      'Unable to delete ShoppingContext from LocalStorage and window object',
      e
    )
  }
}

/**
 * This function is to set the value in a variable to identify storefront or personal offer or my site
 * Jira - https://nuskin.atlassian.net/browse/CX16-9756
 *
 * @date 2023/8/17 - 13:57:08
 *
 */

function setMysiteOrPersonalOffer() {
  if (IS_BROWSER) {
    let isMySiteUrl = checkIsMySiteUrl()
    const storefrontJson = getSessionStorage('storefront')
    if (
      (isMySiteUrl || isRedesignPOUrl()) &&
      (storefrontJson || isRedesignPOUrl())
    ) {
      const shoppingContext =
        isRedesignMysiteUrl() || isRedesignPOUrl()
          ? getSessionStorage('shoppingContext')
          : getLocalStorage('shoppingContext') || {}
      window.shoppingContextJson = shoppingContext
    }
  }
}

/**
 * Splits a full name string into first and last names.
 *
 * Trims the input string, then splits on the last space.
 * Returns an object with `firstName` and `lastName` properties.
 * Returns empty strings if input is invalid.
 */
function getNamefromFirstname(name) {
  try {
    name = name?.trim()
    return {
      firstName:
        name?.lastIndexOf(' ') > 0
          ? name?.slice(0, name?.lastIndexOf(' '))
          : name,
      lastName: name?.slice(name.lastIndexOf(' ') + 1),
    }
  } catch (e) {
    // have proper console with the message
    return {
      firstName: '',
      lastName: '',
    }
  }
}

/**
 * Gets an image URL from a Bynder asset.
 *
 * @param {Object} options - Options object
 * @param {Object} options.bynderImage - Bynder asset object
 * @param {string} [options.defaultType='Small'] - Default image type/size to return
 * @returns {string} Image URL
 */
function getBynderImage(options) {
  const {
    bynderImage = {},
    defaultType = 'transformBaseUrl',
    imageQueryParam,
  } = options || {}
  // const imageType = isWebImageType ? 'transformBaseUrl' : defaultType
  const backupImageType = 'Small'
  let defaultImage =
    bynderImage?.derivatives?.[defaultType] ||
    bynderImage?.derivatives?.[backupImageType]
  let finalImageUrl = ''

  finalImageUrl =
    defaultImage ||
    bynderImage?.derivatives?.webImage ||
    bynderImage?.derivatives?.Large ||
    bynderImage?.files?.webImage?.url

  if (imageQueryParam && finalImageUrl) {
    finalImageUrl = finalImageUrl + imageQueryParam
  }

  return finalImageUrl
}

/**
 * Gets a footer image URL from a Bynder asset.
 *
 * @param {Object} options - Options object
 * @param {Object} options.bynderImage - Bynder asset object
 * @param {string} [options.defaultType='Vector to PNG'] - Default image type/size to return
 * @returns {string} Image URL
 */
function getFooterBynderImage(options) {
  const { bynderImage = {}, defaultType = 'Vector to PNG' } = options || {}
  const defaultImage = bynderImage?.derivatives?.[defaultType]
  return (
    defaultImage ||
    bynderImage?.derivatives?.webImage ||
    bynderImage?.derivatives?.Large ||
    bynderImage?.files?.[defaultType]?.url ||
    bynderImage?.files?.webImage?.url ||
    bynderImage?.selected?.url
  )
}
/**
 * Gets the alt text for a Bynder image from its metadata.
 *
 * @param {Object} options - Options object
 * @param {Object} options.meta - Bynder image metadata
 * @returns {string} Image alt text
 */

function getBynderAltText(options) {
  const { meta = {} } = options || {}
  return meta?.SEO_Tags || ''
}

/**
 * Gets the source of the alt text for a Bynder image from its metadata.
 *
 * @param {Object} options - Options object
 * @param {Object} options.meta - Bynder image metadata
 * @returns {string} Source of image alt text ('bynder' if from Bynder metadata, '' if not)
 */
function getBynderAltTextSrc(options) {
  const { meta = {} } = options || {}
  if (meta || meta?.SEO_Tags) {
    return 'bynder'
  } else {
    return ''
  }
}

/**
 * Tracks an error in Instana.
 *
 * @param {Object} options - Options object
 * @param {string} options.errorReport - The error report name/key
 * @param {Object} options.errorData - Any additional error data to log
 */
function trackErrorInInstana(options) {
  const { errorReport, errorData } = options || {}

  const { monitoring } = APPConfig?.getAppConfig() || {}
  const { enableSplunk } = monitoring || {}
  const isToEnableSplunk = convertToBoolean(enableSplunk) || false

  console.warn(errorReport, errorData)
  if (IS_BROWSER && typeof window?.ineum === 'function') {
    window.ineum('reportEvent', `${errorReport}`, {
      meta: errorData,
    })
  }

  if (isToEnableSplunk) {
    try {
      trackSplunkCustomEvent({
        eventName: errorReport,
        attributes: errorData,
      })
    } catch (e) {
      console.error('Fn trackSplunkCustomEvent ', e)
    }
  }
}

/**
 * Filters out emails that match configured white label domains.
 *
 * @param {Object} options - Options object
 * @param {string} options.email - Email to filter
 * @returns {string} Email without white label domain if filtered
 */
function filterWhiteLabelEmails(options) {
  const { email = '' } = options || {}
  // Multiple emails can be configured using comma separated values
  const { blockedEmailList = '' } = APPConfig.getAppConfig()
  let fitleredEmail = ''
  if (email) {
    const blockListedEmails = blockedEmailList?.split(',')
    const isWhiteLabeledEmail = blockListedEmails.find(emailSuffix =>
      email?.includes(emailSuffix)
    )
    if (!isWhiteLabeledEmail) {
      fitleredEmail = email
    }
  }
  return fitleredEmail
}

/**
 * Converts a standard time string to 12-hour format with AM/PM.
 *
 * @param {string} standardTime - The time in 24-hour format, e.g. "13:45"
 * @returns {string} The time in 12-hour format with AM/PM, e.g. "1:45 PM"
 */
function convertTime(standardTime) {
  try {
    let time = standardTime
      ?.toString()
      .match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [standardTime]
    if (time != null && time?.length > 1) {
      time = time?.slice(1)
      time[5] = +time?.[0] < 12 ? ' AM' : ' PM'
      time[0] = +time?.[0] % 12 || 12
    }
    return time.join('')
  } catch (e) {
    console.log('error while converting standardHours in shipping method')
    return standardTime
  }
}

/**
 * Capitalize first letter of given string.
 * @date 10/11/2023 - 10:03:40 PM
 *
 * @param {string} type
 * @returns {string}
 */
function capitalizeFirstLetter(type) {
  return type?.charAt?.(0)?.toUpperCase() + type?.slice?.(1)?.toLowerCase()
}

/**
 * @description Checks if the page needs to be redirected based on the redirectUrls config
 * and returns redirect url
 *
 * @param {Object} options - The options object
 * @param {string} options.locale - The locale it needs to be redirected to
 * @param {string} options.pageUrl - The current page URL
 * @param {Array<string>} options.req - Express request
 * @returns {string} returns url if it matches the JSON
 */
function getRedirectUrl(options) {
  const { req, pageUrl, locale = '', host } = options || {}
  const appConfig = APPConfig?.getAppConfig()
  const pageLocale = locale || getLocaleCodeFromUrl()
  const localeInUrl = pageLocale?.toLowerCase()?.split('_')
  const currentUrlLocale = `${localeInUrl[1]}/${localeInUrl[0]}`
  let pathName = pageUrl?.replace(
    currentUrlLocale + (IS_BROWSER ? '' : '/'),
    ''
  )
  pathName = pathName?.split('?')?.[0]

  const urlList = Object.keys(appConfig?.redirectUrls || {})
  const redirectUrl = urlList.find(url => {
    return pathName?.toLowerCase() == url?.toLowerCase()
  })

  if (redirectUrl) {
    if (IS_BROWSER) {
      return appConfig?.redirectUrls[redirectUrl]
    }
    const domain = `${req.protocol}://${host}`
    return `${domain}/${currentUrlLocale}${appConfig?.redirectUrls[redirectUrl]}`
  }
}

/**
 * Calculates the total number of pages for pagination based on the total count,
 * page size, and other pagination info.
 *
 * @param {Object} pageableInfo - The pagination info object
 * @param {number} pageableInfo.totalCount - The total number of items
 * @param {number} pageableInfo.page - The current page number
 * @param {number} pageableInfo.size - The page size
 * @param {number} pageableInfo.count - Additional count, default 1
 * @returns {number} The total number of pages
 */
function getTotalPagesForPagination(pageableInfo = {}) {
  const { totalCount = 0, page = 1, size = 0, count = 1 } = pageableInfo || {}
  let totalPages = 0
  try {
    let remainder = totalCount % size
    let remainderCount = 0
    if (remainder != 0 && remainder < size / 2) remainderCount = 1
    let quotient = totalCount / size
    let quotientCount = Math.round(quotient)

    totalPages = remainderCount + quotientCount
  } catch (e) {
    totalPages = 0
  }
  return totalPages
}

/**
 * @description modifyAndGetCustomerType function returns the customer type
 * @date 12/8/2023
 *
 * @param {*} customertypes
 * @returns {*}
 */
function modifyAndGetCustomerType(customertypes = '') {
  let customerTypesData = customertypes?.toLowerCase() || ''
  customerTypesData = customerTypesData?.replace(
    'preferred',
    'preferredcustomer/member'
  )
  customerTypesData = customerTypesData?.replace('retail', 'retailcustomer')
  return customerTypesData
}

/**
 * @description modifyAndGetAccountType function returns account type
 * @date 12/8/2023
 *
 * @param {*} accType
 * @returns {*}
 */
function modifyAndGetAccountType(accType = '') {
  let accTypeData = accType?.replace(/ +/g, '').toLowerCase() || ''
  accTypeData = accTypeData?.includes('brandaffiliate')
    ? 'brandaffiliate'
    : accTypeData
  return accTypeData
}

/**
 * @description checkCustomerTypeEligibility function checks if the customer type
 * is eligible for the account type
 * @param {string} customerTypeResult - The customer type
 * @param {string} accoutTypeResult - The account type
 * @returns {boolean} Returns true if customer type is eligible for account type
 */
function checkCustomerTypeEligibility(
  customerTypeResult = '',
  accoutTypeResult = ''
) {
  return (
    customerTypeResult?.includes(accoutTypeResult) ||
    customerTypeResult === '' ||
    accoutTypeResult?.includes('brandaffiliate') ||
    false
  )
}

function notAllowDangerousGoodsToCart() {
  return (
    convertToBoolean(APPConfig.getAppConfig()?.allowDangerousGoodsToCart) ===
    false
  )
}

/**
 * @description getInventoryMessage function returns an inventory message
 * to display in UI , based on the given product details
 * @date 12/7/2023
 * @param {*} prodInfo
 * @returns {string} inventoryMessage
 */
function getInventoryMessage(itemInfo = {}, accountType = '') {
  const { specialProducts = [], otherProperties = {} } = itemInfo || {}
  const prodInfo = otherProperties || {}
  const isStockError =
    prodInfo?.inventory?.toLowerCase() === 'out of stock' || false // out of stock
  let customerTypeResult =
    modifyAndGetCustomerType(prodInfo?.properties?.customerTypes) || ''
  let isUserType =
    customerTypeResult?.includes(accountType) ||
    customerTypeResult === '' ||
    accountType?.includes('brandaffiliate') ||
    false //user type based restriction
  const availableChannels =
    prodInfo?.availableChannels || prodInfo?.properties?.availableChannels || []
  const isEnableBuyItAgain =
    checkEnableButton(availableChannels, getWebAvailableChannel()) || false //checking available channels
  const dangerousGoods =
    ((prodInfo?.dangerousGoods && prodInfo?.dangerousGoods === 'true') ||
      (prodInfo?.properties?.dangerousGoods &&
        prodInfo?.properties?.dangerousGoods === 'true') ||
      false) &&
    notAllowDangerousGoodsToCart()

  const productStatusCase = () => {
    const productStatus = isBundleHasDiscontinuedSubItem(specialProducts)
      ? 'discontinued'
      : prodInfo?.productStatus || prodInfo?.properties?.productStatus || ''

    switch (productStatus.toLowerCase()) {
      case 'replacement':
        return i18nTranslate(
          'bundle.replacement',
          'This is a replacement product and not available for purchase',
          {
            nameSpace: 'ssr-resource',
          }
        )
      case 'preview product':
      case 'preview':
        return i18nTranslate(
          'bundle.preview',
          'Preview product and will be launched soon',
          {
            nameSpace: 'ssr-resource',
          }
        )
      case 'stopped':
        return i18nTranslate(
          'bundle.stopped',
          'This product is temporarily stopped',
          {
            nameSpace: 'ssr-resource',
          }
        )
      case 'discontinued':
        return i18nTranslate(
          'bundle.discontinuedV2Message',
          'Discontinued - This product or promotion has been discontinued',
          {
            nameSpace: 'ssr-resource',
          }
        )
      case 'bundleonly':
        return i18nTranslate(
          'pdp.bundleOnly',
          'Item not available for individual purchase',
          { nameSpace: 'ssr-resource' }
        )
    }
  }
  switch (true) {
    case isStockError:
      return i18nTranslate('cart.outofstock', 'OUT OF STOCK') || ''
    case !isUserType:
      return i18nTranslate(
        'product.userType',
        'This user is not eligible to purchase this product'
      )
    case !isEnableBuyItAgain:
      return i18nTranslate(
        'order.history.product.not.available',
        'This product is no-longer available to purchase through WEB'
      )
    case dangerousGoods:
      return i18nTranslate(
        'pdp.dangerousGoodsError',
        'Dangerous Goods cannot be added to Cart or Subscribed',
        {
          nameSpace: 'ssr-resource',
        }
      )
    default:
      return productStatusCase()
  }
}

/**
 * Checks if a bundle has any discontinued sub-items.
 * @param {Object[]} specialProducts - An array of special product objects.
 * @param {string} specialProducts[].otherproperties.productStatus - The product status of the special product.
 * @returns {boolean} - True if the bundle has any discontinued sub-items, false otherwise.
 */
function isBundleHasDiscontinuedSubItem(specialProducts = []) {
  return (
    (Array.isArray(specialProducts) &&
      specialProducts.length > 0 &&
      specialProducts?.some(
        specialProduct =>
          specialProduct?.otherproperties?.productStatus?.toLowerCase() ===
          'discontinued'
      )) ||
    false
  )
}

/**
 * Returns the status of the fraud detection callback.
 * Checks if the browser window has a property indicating
 * the fraud detection script has loaded, and returns
 * that value. Falls back to false if not present.
 */
function getFraudDetectionCbkStatus() {
  if (IS_BROWSER) {
    return window?.isFraudDetectionScriptLoaded ?? false
  }
  return false
}

/**
 * Sets the status of the fraud detection callback in the browser window.
 * This allows other code to check if the fraud detection script has loaded.
 * @param {boolean|string} status - The status to set. If a string, must be 'true'.
 */
function setFraudDetectionCbkStatus(status) {
  if (IS_BROWSER) {
    window.isFraudDetectionScriptLoaded = status == 'true' || status == true
  }
}

/**
 * @description getBundleReorderInfo : to get if bundle products are eligible
 * @date 12/11/2023 - 8:20:11 PM
 *
 * @param {*} reorderItems
 * @returns {*}
 */
function getBundleReorderInfo(reorderItems = []) {
  const bundleFilterInfo =
    reorderItems?.filter(
      element => element?.item?.itemInfo?.specialProducts?.length > 0
    ) || []
  const bundleInfo =
    bundleFilterInfo?.map(value => {
      return value?.item?.itemInfo?.specialProducts
    }) || []
  const checkForBundleReorder = bundleInfo?.map(value => {
    return (
      value?.map(val => {
        return val?.inventoryProperties?.available
      }) || []
    )
  })

  let bundleCheckArray = []
  checkForBundleReorder?.forEach(element => {
    bundleCheckArray?.push(element?.some(val => val === false))
  })

  const checkIfBundleIsEligible =
    bundleCheckArray?.some(element => element === false) || false

  return {
    checkIfBundleIsEligible: checkIfBundleIsEligible,
    checkForBundleReorder: checkForBundleReorder,
  }
}

/**
 * @description checkProductBackOrdered function checks whether the given set of products is backordered or not
 * @date 12/21/2023 - 4:41:02 PM
 *
 * @param {{}} [orderItems={}]
 * @returns {{}}
 */
function checkProductBackOrdered(orderItems = {}) {
  const { specialProducts = {}, sku = {} } = orderItems?.item?.itemInfo || {}
  const singleProductBackOrderedStatus =
    sku?.inventoryProperties?.backOrdered || false
  const bundleBackOrdersDate = []
  let isBundleBackOrdered = false
  const bundleBackOrdered =
    (specialProducts?.length > 0 &&
      specialProducts?.forEach(value => {
        isBundleBackOrdered = value?.inventoryProperties?.backOrdered || false
        if (isBundleBackOrdered) {
          bundleBackOrdersDate?.push(
            value?.inventoryProperties?.expectedBackOrderAvailabilityDate || ''
          )
        }
      })) ||
    []
  const comparebundleBackOrdersDate = bundleBackOrdersDate?.sort(
    (a, b) => b - a
  )
  let getBundleBackOrderedInventory = {}
  if (bundleBackOrdersDate?.length > 0) {
    getBundleBackOrderedInventory =
      specialProducts?.filter(
        value =>
          value?.inventoryProperties?.expectedBackOrderAvailabilityDate ===
          comparebundleBackOrdersDate?.[0]
      ) || {}
  }

  return {
    bundleInfo: specialProducts,
    isBundleBackOrdered: isBundleBackOrdered,
    singleProductBackOrderedStatus: singleProductBackOrderedStatus,
    getBundleBackOrderedInventory: getBundleBackOrderedInventory,
  }
}

function getOrderHistoryBackOrderedDate(orderItem = {}) {
  const {
    specialProducts = {},
    otherProperties = {},
    productType = '',
    sku = {},
  } = orderItem?.item?.itemInfo || {}

  const productionBom = convertToBoolean(
    otherProperties?.productionBom || false
  )
  const isPbomEnable = convertToBoolean(
    APPConfig?.getAppConfig()?.enablePbomFeature
  )

  const isPBomProduct =
    productionBom &&
    isPbomEnable &&
    (productType?.toLowerCase() === 'kit' ||
      productType?.toLowerCase() === 'skukit')

  let singleProductBackOrderedStatus = false
  let isBundleBackOrdered = false
  let getBundleBackOrderedInventory = {}
  let backOrderDateNew = ''

  if (productType.toLowerCase() === 'default') {
    const isoDate = sku?.inventoryProperties?.backOrderISODate
    backOrderDateNew = isoDate
      ? String(new Date(isoDate).getTime())
      : otherProperties?.backOrderDate || ''
  } else {
    const bundleBackOrdersDate = []
    const isBackOrdered = item =>
      item?.otherproperties?.backOrderDate !== '' ? true : false
    isBundleBackOrdered = isPBomProduct
      ? sku?.inventoryProperties?.backOrdered
      : specialProducts?.some(isBackOrdered)

    let bundleBackOrdered = []
    if (isPBomProduct && isBundleBackOrdered) {
      bundleBackOrdersDate?.push(
        sku?.inventoryProperties?.expectedBackOrderAvailabilityDate || ''
      )
    } else {
      bundleBackOrdered =
        specialProducts?.length > 0 &&
        specialProducts?.forEach(value => {
          let isItemBackOrdered =
            value?.otherproperties?.backOrderDate !== '' || false
          if (isItemBackOrdered) {
            bundleBackOrdersDate?.push(
              value?.otherproperties?.backOrderDate || ''
            )
          }
        })
    }
    const comparebundleBackOrdersDate = bundleBackOrdersDate?.sort(
      (a, b) => b - a
    ) //sorting backorder dates as descending when all special products are backorered

    if (bundleBackOrdersDate?.length > 0) {
      if (isPBomProduct) {
        getBundleBackOrderedInventory = [
          {
            otherproperties: {
              backOrderDate:
                sku?.inventoryProperties?.expectedBackOrderAvailabilityDate,
            },
          },
        ]
      } else {
        getBundleBackOrderedInventory =
          specialProducts?.filter(
            value =>
              value?.otherproperties?.backOrderDate ===
              comparebundleBackOrdersDate?.[0]
          ) || {}
      }
    }
  }

  return {
    bundleInfo: specialProducts,
    isBundleBackOrdered: isBundleBackOrdered,
    singleProductBackOrderedStatus: sku?.inventoryProperties?.backOrdered,
    getBundleBackOrderedInventory: getBundleBackOrderedInventory,
    backOrderDate: backOrderDateNew,
  }
}

/**
 * @description isGPSEnabled function checked APPConfig flag enableGlobalPaymentSystem and returns true/false
 * @returns {boolean}
 */
function isGPSEnabled() {
  return APPConfig?.getAppConfig()?.enableGlobalPaymentSystem == 'true'
}
function enableMultipleSubscription() {
  return APPConfig?.getAppConfig()?.enableMultipleSubscription == 'true'
}

// /**
//  * @description isGPSDefaultPayment function checked APPConfig flag enableDefaultPayment and returns true if GPS is default, else false
//  * @returns {boolean}
//  */
// function isGPSDefaultPayment() {
//   return APPConfig?.getAppConfig()?.enableDefaultPayment?.toLowerCase() == 'gps'
// }

/**
 * @description getParsedPhoneNumber function parses a phone number string into a formatted national number and country code using the libphonenumber-js library
 * @param {string} phoneNumber - The phone number to parse
 * @returns {Object} - An object containing the formatted national number and country code
 *    phoneNumberCode - The formatted national phone number
 *    phoneCountry - The country code
 */
async function getParsedPhoneNumber(phoneNumber) {
  try {
    const { parsePhoneNumber } = await import('libphonenumber-js')
    const parsedPhone = parsePhoneNumber(phoneNumber)
    const phoneCountry = parsedPhone?.country

    const nationalFormat = convertToBoolean(
      APPConfig?.getAppConfig()?.nationalPhoneNumberFormat
    )

    // Use nationalNumber for Indian phone numbers to avoid leading "0" when flag is enabled
    // Use formatNational for all other countries for proper formatting
    const phoneNumberCode = nationalFormat
      ? parsedPhone?.nationalNumber || phoneNumber
      : parsedPhone?.formatNational() || phoneNumber

    return { phoneNumberCode: phoneNumberCode, phoneCountry: phoneCountry }
  } catch (error) {
    return { phoneNumberCode: '', phoneCountry: '' }
  }
}

/**
 * Display Order Return Based on the flag supported Locales we configured in defaultConfig
 * For Ex: if we want to display in US and not for CA, means orderReturnSupportedLocales will be ["US"]
 * @date 2/2/2024 - 12:39:26 PM
 *
 * @returns {true|false} - returns true if current locale is matched with orderReturnSupportedLocales config and
 * return button will be displayed
 * else if it returns false, return button will not be displayed
 */
function displayOrderReturnForSupportedLocales() {
  const selectedLocaleCode =
    getLocaleCodeFromUrl({
      defaultLocale: APPConfig?.getActiveAppConfig()?.defaultLocale,
    })?.split('_')?.[1] || 'US'
  const orderReturnSupportedLocales = APPConfig?.getAppConfig()
    ?.orderReturnSupportedLocales || ['US']
  const isToDisplayReturnButton =
    orderReturnSupportedLocales?.includes(selectedLocaleCode)
  return isToDisplayReturnButton
}

/**
 * Validates email format with TLD whitelist and Unicode character checks.
 *
 * @param {string} emailValue - The email address to validate
 * @returns {boolean} True if email is valid, false otherwise
 */

function emailValidation(emailValue) {
  try {
    const email = String(emailValue).toLowerCase().trim()
    // Check for diacritic and Unicode characters that should not be allowed
    const diacriticRegex =
      /[À-ÖØ-öø-įĴ-őŔ-žǍ-ǰǴ-ǵǸ-țȞ-ȟȤ-ȳɃɆ-ɏḀ-ẞƀ-ƓƗ-ƚƝ-ơƤ-ƥƫ-ưƲ-ƶẠ-ỿ]/
    const greekRegex = /[α-ωΑ-Ω]/
    const chineseRegex = /[一-龥]/
    const japaneseRegex = /[ぁ-ゔァ-ヴー]/
    const cyrillicRegex = /[А-Яа-яЁё]/
    const devanagariRegex = /[अ-ह]/
    // Reject emails with Unicode/diacritic characters
    if (
      diacriticRegex.test(email) ||
      greekRegex.test(email) ||
      chineseRegex.test(email) ||
      japaneseRegex.test(email) ||
      cyrillicRegex.test(email) ||
      devanagariRegex.test(email)
    ) {
      return false
    }
    // Email regex pattern from config with specific TLD whitelist
    const emailRegexPattern =
      APPConfig?.getAppConfig()?.emailRegex ||
      '/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\\.(?:com|org|net|in|io|dev|app|co|uk|co\\.uk|gov\\.in|ca|cl|edu)$/i'

    const regexMatch = emailRegexPattern.match(/^\/(.*)\/([gimuy]*)$/)
    if (!regexMatch) {
      return false
    }

    const emailRegex = new RegExp(regexMatch[1], regexMatch[2])
    return emailRegex.test(email)
  } catch (e) {
    return false
  }
}

/**
 * @description Checks if guest checkout is enabled for the web based on the configuration.
 * @returns {boolean} - True if guest checkout is enabled, false otherwise.
 */
function isGuestCheckoutEnabledForWeb() {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableGuestCheckoutForWeb)
}

function getGPSConfigValue(configKey) {
  return APPConfig?.getAppConfig()?.gpsConfig?.[configKey] || ''
}

function isToEnableEQGuestFastFlow() {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.enableEQFastFollow || false
  )
}

/**
 * @description getLiveEventStatus function checks defaultConfig for enableLiveEvent flag and returns true/false
 * @returns {boolean}
 */

function getLiveEventStatus() {
  return APPConfig?.getAppConfig()?.enableLiveEvent || 'false'
}

function getFavoritesEnabled() {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableFavorites)
}

function getWebAvailableChannel() {
  const enableLiveEvent = getLiveEventStatus()
  return convertToBoolean(enableLiveEvent) ? 'liveEvent' : 'web'
}

/**
 * @description getEventNotificationTimeline function return Event Notification Timeline Array from config
 */

function getEventNotificationTimeline() {
  return APPConfig?.getAppConfig()?.eventAlertTimeline || []
}

const LIVE_EVENT_DISABLED_ACTION_KEYS = new Set([
  'beforeEvent',
  'afterEvent1',
  'afterEvent2',
  'eventOffline',
])

function isLiveEventActionDisabledByKey(currentEventKey = '') {
  return LIVE_EVENT_DISABLED_ACTION_KEYS.has(currentEventKey)
}

function getCurrentLiveEventTimelineKey(eventNotificationTimeline = []) {
  let latestStartTime = null
  let translationKey = ''

  for (let i = 0; i < eventNotificationTimeline?.length; i++) {
    const eventNode = eventNotificationTimeline[i] || {}
    const timeZone = eventNode?.timeZone || 'US/Mountain'
    const currentTime = new Date(
      new Date().toLocaleString('en-US', { timeZone })
    )
    const startTime = new Date(eventNode?.startTime)
    const endTime = new Date(eventNode?.endTime)

    if (startTime <= currentTime && currentTime <= endTime) {
      if (latestStartTime === null || startTime >= latestStartTime) {
        latestStartTime = startTime
        translationKey = eventNode?.translationKey || ''
      }
    }
  }

  return translationKey
}

function shouldDisableLiveEventCartAction() {
  const enableCartForLiveEvent =
    APPConfig?.getAppConfig()?.enableCartForLiveEvent || 'false'
  const eventNotificationTimeline = getEventNotificationTimeline()
  const currentEventKey = getCurrentLiveEventTimelineKey(
    eventNotificationTimeline
  )

  if (convertToBoolean(enableCartForLiveEvent)) {
    return false
  }
  return isLiveEventActionDisabledByKey(currentEventKey)
}

/**
 * @description Checks app config for hideRegionalTaxLabel flag and returns true/false.
 * @returns {boolean}
 */
function hideRegionalTaxLabel() {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.hideRegionalTaxLabel || 'false'
  )
}

/**
 * @description getShouldEnableProfileEditInEvent function checks defaultConfig for enableProfileEditInEvent flag and returns true/false
 * @returns {boolean}
 */
function getShouldEnableProfileEditInEvent() {
  return APPConfig?.getAppConfig()?.enableProfileEditInEvent || 'false'
}

/**
 * @description isNonEventOrEventWithSub returns true by default if non event site
 * return value from APPConfig for enableSubscriptionInEvent for event site
 * @returns {boolean}
 */
function isNonEventOrEventWithSub() {
  return (
    !convertToBoolean(getLiveEventStatus()) ||
    convertToBoolean(
      APPConfig?.getAppConfig()?.enableSubscriptionInEvent || 'false'
    )
  )
}

/**
 * @description enablePhoneVerification function checks defaultConfig for enablePhoneVerification flag and returns true/false
 * @returns {boolean}
 */

function enablePhoneVerification() {
  return true
  // removing this as the flag is obsolete and has bugs

  // instead use
  // enablePhoneVerificationForConversion
  // enablePhoneVerificationOnLanding
  // enablePhoneVerificationInProfileUpdate

  // verify button hide is yet to be handled

  // return convertToBoolean(APPConfig?.getAppConfig()?.enablePhoneVerification)
}

function enablePhoneVerificationInProfileUpdate() {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.enablePhoneVerificationInProfileUpdate
  )
}

function enablePhoneVerificationForConversion() {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.enablePhoneVerificationForConversion
  )
}

function enablePhoneVerificationOnLanding() {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.enablePhoneVerificationOnLanding
  )
}

function enableLegalAddress() {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableLegalAddress)
}

function isDobMandatoryForConversion(conversionTargetType = '') {
  const dateOfBirthMandatedTypes =
    APPConfig?.getAppConfig()?.dateOfBirthMandatedTypes || []
  return dateOfBirthMandatedTypes?.includes?.(conversionTargetType)
}

function isDobOptionalForConversion(conversionTargetType = '') {
  const dateOfBirthOptionalTypes =
    APPConfig?.getAppConfig()?.dateOfBirthOptionalTypes || []
  return dateOfBirthOptionalTypes?.includes?.(conversionTargetType)
}

function isDobEnabled(conversionTargetType = '') {
  return (
    isDobMandatoryForConversion(conversionTargetType) ||
    isDobOptionalForConversion(conversionTargetType)
  )
}

function enableLegalAddressForMemConversion() {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.enableLegalAddressForMemConversion
  )
}

function enableLegalTaxIdForMemConversion() {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.enableLegalTaxIdForMemConversion
  )
}

function enableJITSU() {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableJITSU)
}

function enableFeatureFlagtoApi() {
  return convertToBoolean(APPConfig?.getAppConfig()?.isFeatureFlagtoApi)
}

function enableCSErrorCodeMessage() {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableCSErrorCodeMessage)
}

function enableMysiteRedesignFeature() {
  return (
    convertToBoolean(
      APPConfig?.getAppConfig()?.isMysiteRedesignFeatureEnabled
    ) || false
  )
}

function enablePORedesignFeature() {
  return (
    convertToBoolean(APPConfig?.getAppConfig()?.enableProductOfferV2) || false
  )
}

/**
 * Checks if a breakup item has "product offer" as the offer source.
 * @param {Object} breakupItem - The breakup item to check.
 * @returns {boolean} True if offerSource is "product offer".
 */
function hasProductOfferSource(breakupItem) {
  const offerSource = breakupItem?.otherProperties?.offerSource
  // Normalize by removing spaces and converting to lowercase for comparison
  const normalizedOfferSource = offerSource?.replace(/\s+/g, '').toLowerCase()
  return normalizedOfferSource === CONST_PRODUCT_OFFER
}

/**
 * Detects if any cart item has a "product offer" promotion.
 * Loops through cart items, checks promotionDetails.promotions[].breakup[]
 * for otherProperties.offerSource === "product offer".
 *
 * @param {Object} cartResponse - The cart response object.
 * @returns {boolean} true when any item has a product offer promotion.
 */
function hasProductOfferPercentageDiscount(cartResponse = {}) {
  if (!cartResponse) return false
  const items = cartResponse.items || []
  if (!Array.isArray(items) || items.length === 0) return false

  return items.some(item => {
    const promotions = item?.promotionDetails?.promotions
    if (!Array.isArray(promotions)) return false

    return promotions.some(promotion => {
      const breakup = promotion?.breakup
      if (!Array.isArray(breakup)) return false

      return breakup.some(hasProductOfferSource)
    })
  })
}

function enableSafeRedirectHardening() {
  return (
    convertToBoolean(APPConfig?.getAppConfig()?.enableSafeRedirectHardening) ||
    false
  )
}

function showPrysmBadge() {
  return convertToBoolean(APPConfig?.getAppConfig()?.showPrysmBadge) || false
}

function disableLegalMarketCheckboxAccConv() {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.disableLegalMarketCheckboxAccConv
  )
}

function getInputFormJSON(locale) {
  const specialCharacter = /^[a-zA-Z0-9!@#$%^&*)(+=._-]+$/

  const getRegionInputJSON = {
    es_MX: {
      name: {
        regEx: specialCharacter,
        maxLength: 40,
      },
      email: {
        maxLength: 256,
      },
      maskValue: [
        '(',
        /[1-9]/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
      ],
    },
    es_CL: {
      name: {
        regEx: specialCharacter,
        maxLength: 40,
      },
      email: {
        maxLength: 256,
      },
      maskValue: [
        '(',
        /[1-9]/,
        /\d/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        /\d/,
      ],
    },
    es_PE: {
      name: {
        regEx: specialCharacter,
        maxLength: 40,
      },
      email: {
        maxLength: 256,
      },
      maskValue: [
        '(',
        /[0-9]/,
        /\d/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        /\d/,
      ],
    },
    es_AR: {
      name: {
        regEx: specialCharacter,
        maxLength: 40,
      },
      email: {
        maxLength: 256,
      },
      maskValue: [
        '(',
        /[1-9]/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
      ],
    },
    es_CO: {
      name: {
        regEx: specialCharacter,
        maxLength: 40,
      },
      email: {
        maxLength: 256,
      },
      maskValue: [
        '(',
        /[1-9]/,
        /\d/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        /\d/,
      ],
    },
  }

  return getRegionInputJSON[locale] || {}
}

function jitsuSignUpRedirect(options) {
  const {
    isToReturnUrl = false,
    isFromGuestCheckout = false,
    reEnroll = false,
    type,
  } = options || {}
  let currentLocale = getLocaleCodeFromUrl()?.split('_')
  currentLocale =
    '/' +
    currentLocale[1]?.toLowerCase() +
    '/' +
    currentLocale[0]?.toLowerCase()
  const enableJitsuFromStorefront = enableJitsuSF()
  const signUpPath = enableJitsuFromStorefront ? '/signup' : '/site/signup'
  const jitsuUrl =
    `https://${document.domain}${currentLocale}` +
    signUpPath +
    (isFromGuestCheckout
      ? '?origin=checkout'
      : reEnroll
        ? '/?reEnroll=true' + `&type=${type}`
        : '')
  if (isToReturnUrl) {
    return jitsuUrl
  }
  window.location.href = jitsuUrl
}

function signUpRedirectionForDirectSellerPopUp() {
  let currentLocale = getLocaleCodeFromUrl()?.split('_')
  currentLocale =
    '/' +
    currentLocale[1]?.toLowerCase() +
    '/' +
    currentLocale[0]?.toLowerCase()
  const url = `https://${document.domain}${currentLocale}/site/signup#/?importedDevice=true&type=RETAIL`
  window.location.href = url
}

/**
 * Navigates to the sign-up page, respecting JITSU, B2B and B2B2C flows.
 * @param {import('history').History} [history] - React Router history; falls
 *   back to a full-page locale-prefixed assign when absent.
 */
function signUp(history) {
  if (enableJITSU()) {
    jitsuSignUpRedirect()
    return
  }
  const { businessRelationship } = APPConfig.getAppConfig() || {}
  const pageLink =
    businessRelationship === 'B2B' || isB2B2C()
      ? pageNames.signUp
      : pageNames.createAccount
  if (history?.push) {
    history.push(pageLink)
    return
  }
  if (IS_BROWSER) {
    const locale = getLocalePathParam()
    window.location.assign(`${locale ? `/${locale}` : ''}${pageLink}`)
  }
}

/**
 * Navigates to the account page, respecting B2B flow.
 * @param {import('history').History} [history] - React Router history; falls
 *   back to a full-page locale-prefixed assign when absent.
 */
function account(history) {
  const accountPath = isB2BAccount()
    ? pageNames.b2bMyProfile
    : pageNames.myaccountprofile
  if (history?.push) {
    history.push(accountPath)
    return
  }
  if (IS_BROWSER) {
    const locale = getLocalePathParam()
    window.location.assign(`${locale ? `/${locale}` : ''}${accountPath}`)
  }
}

/**
 * Checks the information for an array of SKUs.
 *
 * @param {Object[]} [skus=[]] - An array of SKU objects.
 * @returns {Object[]} - An array of SKU details, including whether the SKU is a normal SKU or a bundle SKU, and an array of SKUs that are not the main SKU.
 */
function checkForSkusArrayInfo(skus = []) {
  const specialProductTypes = [
    'bundle',
    'collection',
    'fixedbundle',
    'skukit',
    'fixedpricebundle',
    'kit',
  ]

  const normalProductSkuType = ['default']

  let skusExceptMainSku = []
  let skuDetails = skus?.map((sku, key) => {
    const { type = '' } = sku || {}
    const skuType = type?.toLowerCase()?.replace('_', '') || ''
    let isNormalSku = normalProductSkuType.includes(skuType)

    let skuInfo = {}
    let isBundleSku = specialProductTypes.includes(skuType)
    if (!isBundleSku && !isNormalSku) {
      skusExceptMainSku.push(sku)
    }

    if (isNormalSku || isBundleSku) {
      return { isNormalSku, isBundleSku, skuInfo: sku, skusExceptMainSku }
    }
  })

  return skuDetails
}

function getCookiesFeatureOverride() {
  const allcookies = typeof window === 'object' ? document?.cookie : ''
  const cookiearray = allcookies.split(';')
  const cookieJSON = {}

  // Now take key value pair out of this array
  for (var i = 0; i < cookiearray.length; i++) {
    const name = cookiearray[i].split('=')[0].trim()
    const value = cookiearray[i].split('=')[1]
    cookieJSON[name] = value
  }
  let getFeatureOverride = ''
  APPConfig?.getAppConfig()?.configPassToAPI?.map(item => {
    if (cookieJSON.hasOwnProperty(item)) {
      if (cookieJSON[item] === true || cookieJSON[item] === 'true') {
        getFeatureOverride =
          getFeatureOverride + (getFeatureOverride === '' ? item : ',' + item)
      } else {
        getFeatureOverride =
          getFeatureOverride +
          (getFeatureOverride === ''
            ? item.replace('enable', 'disable')
            : ',' + item.replace('enable', 'disable'))
      }
    }
  })
  return getFeatureOverride
}

function setStoragesForAccountConversion(accountsContainer = '') {
  if (IS_BROWSER) {
    const pathName = window?.location?.pathname
    const isAccountProfile =
      pathName?.includes(pageNames.b2bAccountTypeConvert) ||
      pathName?.includes(pageNames.b2bAccountProfile)

    if (isAccountProfile) {
      const searchParams = new URLSearchParams(window?.location?.search)
      const type = searchParams?.get('type')?.toLowerCase() || ''
      const sponsor = searchParams?.get('sponsor') || ''

      if (sponsor) {
        if (type === 'member') {
          setSessionStorage('mem', 'ok')
        }
        if (type == 'retail') {
          setSessionStorage('switchsponsor', 'ok')
        }
        setSessionStorage('conv-sponsor', sponsor)
      } else {
        deleteFromSessionStorage('mem')
        deleteFromSessionStorage('conv-sponsor')
        deleteFromSessionStorage('switchsponsor')
        if (
          accountsContainer &&
          accountsContainer.convertAccoutType == 'Preferred Customer/Member'
        ) {
          accountsContainer.convertAccoutType = ''
        }
      }
    }
  }
}

/**
 * Retrieves information about a given sponsor ID.
 *
 * @param {string} sponsorId - The ID of the sponsor to retrieve information for.
 * @returns {object} An object containing information about the sponsor, including whether it is an in-house sponsor or a RetFloat sponsor.
 */
function getSponsorInfo(sponsorId) {
  const sponsorInfo = {
    isInHouseSponsor: false,
    isRetFloat: false,
  }
  const houseSponsor = APPConfig?.getAppConfig()?.inHouseSponsor || {}
  const sponsorValues = Object.values(houseSponsor)
  if (sponsorValues.includes(sponsorId)) {
    sponsorInfo.isInHouseSponsor = true
  }

  if (sponsorId === 'RETFLOAT') {
    sponsorInfo.isRetFloat = true
  }

  return sponsorInfo
}

/**
 * Sets the UTM information in the session storage.
 *
 * This function retrieves the UTM parameters from the current URL and stores them in the session storage under the key 'utmInfo'.
 * The UTM parameters are stored as an object, where the keys are the parameter names and the values are the corresponding parameter values.
 *
 */
function setUTMInfo() {
  try {
    const utmInfo = {}
    const { UTTConfig, enableEQFastFollow = false } = APPConfig?.getAppConfig()
    const { UTTParams = [], cookieExpiryInSeconds = 2592000 } = UTTConfig

    if (IS_BROWSER) {
      const isFromUTT = UTTParams.some(UTTParam =>
        location.search.includes(UTTParam)
      )
      const isFromUTM = location.search?.includes('utm_')

      const queryParams = new URLSearchParams(location.search)
      queryParams.forEach((value, key) => {
        utmInfo[key] = convertToBoolean(enableEQFastFollow)
          ? stripOffExtraDoubleQuotes(value)
          : value
      })

      if (isFromUTT) {
        setCookie({
          cookieName: 'utmInfo',
          cookieValue: JSON.stringify(utmInfo),
          expirySeconds: cookieExpiryInSeconds,
          isSessionCookie: cookieExpiryInSeconds == 0,
        })
        deleteFromSessionStorage('utmInfo')
      } else if (isFromUTM) {
        setSessionStorage('utmInfo', utmInfo)
      }
    }
  } catch (e) {
    trackErrorInInstana({ errorReport: 'Error in setUTMInfo', errorData: e })
  }
}

/**
 * Retrieves the UTM information stored in the session storage.
 *
 * This function retrieves the UTM parameters that were previously stored in the session storage using the `setUTMInfo()` function.
 *
 * @returns {object} An object containing the UTM parameters,
 * where the keys are the parameter names and the values are the corresponding parameter values.
 */
function getUTMInfo() {
  try {
    const UTTCookieValues = getCookie('utmInfo')
    if (UTTCookieValues) {
      const cookieValues = JSON.parse(UTTCookieValues)
      if (cookieValues) {
        // Loop through object and strip quotes from each value
        Object.keys(cookieValues).forEach(key => {
          cookieValues[key] = stripOffExtraDoubleQuotes(cookieValues[key])
        })
      }
      return cookieValues
    } else {
      const utmInfo = getSessionStorage('utmInfo')
      return utmInfo
    }
  } catch (e) {
    trackErrorInInstana({ errorReport: 'Error in getUTMInfo', errorData: e })
  }
}

/**
 * Retrieves the UTT information stored in the session storage.
 *
 * This function retrieves the UTT parameters that were previously stored in the session storage using the `getUTTInfo()` function.
 *
 * @returns {object} An object containing the UTT parameters,
 * where the keys are the parameter names and the values are the corresponding parameter values.
 */
function getUTTInfo() {
  try {
    const UTTCookieValues = getCookie('utmInfo')
    if (UTTCookieValues) {
      const uttInfo = {}
      const uttCookieJSON = JSON.parse(UTTCookieValues) || {}
      const { UTTConfig = {} } = APPConfig?.getAppConfig()
      const { UTTParamsMapping = {}, cookieExpiryInSeconds = 2592000 } =
        UTTConfig
      const UTTParamsMappingKeys = Object.keys(UTTParamsMapping) || []

      const uttCookieJSONKeys = Object.keys(uttCookieJSON)
      uttCookieJSONKeys.forEach(key => {
        UTTParamsMappingKeys.forEach(val => {
          if (UTTParamsMapping[val] === key) {
            uttInfo[val] = uttCookieJSON[key]
          }
        })
      })

      return uttInfo
    }

    return {}
  } catch (e) {
    trackErrorInInstana({ errorReport: 'Error in getUTTInfo', errorData: e })
  }
}

/**
 * Retrieves queryParam with strip off the extra ““ when we capture the utm / utt data from the url before passing this information
 */
function stripOffExtraDoubleQuotes(value) {
  try {
    return value?.replace(/^\"|\"$/g, '')
  } catch (e) {
    trackErrorInInstana({
      errorReport: 'Error in strip off the extra Double Quotes',
      errorData: e,
    })
  }
}

/**
 * Retrieves the sponsor ID based on the UTM source or a default value.
 *
 * This function checks the UTM information stored in the session storage to determine the sponsor ID.
 * If a UTM source is present, it looks up the corresponding sponsor ID in the `inHouseSponsor` configuration.
 * If no UTM source is found, it returns the default sponsor ID of 'RETFLOAT'.
 *
 * @returns {string} The sponsor ID based on the UTM source or the default value.
 */
function getGuestSponsorId() {
  const utmInfo = getUTMInfo()
  const { inHouseSponsor } = APPConfig?.getAppConfig()
  let sponsorId = ''

  if (utmInfo?.utm_medium) {
    sponsorId =
      inHouseSponsor[utmInfo?.utm_medium] || inHouseSponsor?.utmDefault
  } else {
    sponsorId = inHouseSponsor?.defaultSponsor
  }
  return sponsorId
}

/**
 * Retrieves the signup origin based on the user's shopping context and UTM information.
 *
 * This function determines the signup origin for a guest user based on the following logic:
 * - If the user is on the Mysite storefront, the signup origin is 'Mysite'.
 * - If the user is on a personal offer page, the signup origin is 'PersonalOffer'.
 * - If the user has a UTM source, the signup origin is the UTM source.
 * - If none of the above conditions are met, the signup origin is 'Web'.
 *
 * @returns {string} The signup origin for the guest user.
 */
function getGuestSignupOrigin() {
  let signupOrigin = 'Web'
  let shoppingContext = ''
  const isGuestCheckoutEnabled = isGuestCheckoutEnabledForWeb()

  if (IS_BROWSER) {
    shoppingContext = checkMysiteOrPersonalOffer() || ''
    if (shoppingContext === 'storefront') {
      signupOrigin = 'Mysite'
    } else if (shoppingContext === 'personal_offer') {
      signupOrigin = 'PersonalOffer'
    }
  }
  // Commented for recent request  from PUR-1124
  // if (isGuestCheckoutEnabled && isFromAccounts) {
  //   const utmInfo = getUTMInfo()
  //   const utmSource = utmInfo?.utm_medium
  //   if (utmSource) {
  //     signupOrigin = utmSource
  //   }
  // }
  return signupOrigin
}

function delay(ms = 0) {
  return new Promise(res => setTimeout(res, ms))
}

function isExpressCheckout(useExpressCart = false) {
  if (IS_BROWSER) {
    const { enableExpressCheckout, enableBuyNowInPDP } =
      APPConfig?.getAppConfig()

    const isSubscriptionOverrideCheckout =
      isSubscriptionOverrideCheckoutPage() || false
    if (isSubscriptionOverrideCheckout) {
      return true
    }

    if (
      getLocalStorage('isExpressCart') &&
      convertToBoolean(enableExpressCheckout)
    ) {
      return true
    }

    if (
      getLocalStorage('isExpressPDPLogin') &&
      convertToBoolean(enableExpressCheckout)
    ) {
      return true
    }
    if (
      location.href?.includes(pageNames.product + '/') &&
      convertToBoolean(enableExpressCheckout) &&
      convertToBoolean(enableBuyNowInPDP)
    ) {
      return true
    }
    if (useExpressCart) {
      return (
        convertToBoolean(enableExpressCheckout) &&
        location.href?.includes(
          pageNames.expressPDP || location.href?.includes(pageNames.product)
        )
      )
    }
    if (
      !useExpressCart &&
      location.href?.includes(
        pageNames.expressPDP || location.href?.includes(pageNames.product)
      ) &&
      convertToBoolean(enableExpressCheckout)
    ) {
      return true
    }
    return (
      (convertToBoolean(enableExpressCheckout) &&
        location.href?.includes(pageNames.expressCheckout)) ||
      (enableExpressForRegistered() &&
        location.href?.includes(pageNames.checkout))
    )
  }
  return false
}

function isExpressCart(useExpressCart = false) {
  if (IS_BROWSER) {
    const { enableExpressCheckout } = APPConfig?.getAppConfig()
    if (
      getLocalStorage('isExpressCart') &&
      convertToBoolean(enableExpressCheckout)
    ) {
      return true
    }
    if (
      !getLocalStorage('isExpressCart') &&
      convertToBoolean(enableExpressCheckout)
    ) {
      return false
    }
  }
  return false
}

/**
 * Generates Google Tag Manager (GTM) page data for checkout pages.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.pageTitle=''] - The title of the page.
 * @param {boolean} [props.skipTitle=false] - Flag to skip setting the page title.
 * @returns {Object} The GTM page data object containing `pagePath` and `pageTitle`.
 */
function getCheckoutGTMPageData(props) {
  const gtmPageData = {}
  if (IS_BROWSER) {
    const possibleCheckoutPages = [
      { pathName: pageNames.checkout, pageTitle: 'Checkout' },
      { pathName: pageNames.expressCheckout, pageTitle: 'Express Checkout' },
      { pathName: pageNames.expressPDP, pageTitle: 'Express PDP' },
      {
        pathName: pageNames.subscriptionCheckout,
        pageTitle: 'Subscription Checkout',
      },
    ]
    const currentPage = possibleCheckoutPages.find(page =>
      location?.pathname?.includes(page.pathName)
    )
    gtmPageData['pagePath'] = currentPage?.pathName || pageNames.checkout
    if (!props?.skipTitle)
      gtmPageData['pageTitle'] =
        props?.pageTitle || currentPage?.pageTitle || 'Checkout'
    return gtmPageData
  }
  return {
    pagePath: pageNames.checkout,
    pageTitle: props?.pageTitle || 'Checkout',
  }
}

async function encodeSHA1(data) {
  try {
    const encoder = new TextEncoder()
    const dataBuffer = encoder.encode(data)
    const hashBuffer = await crypto.subtle.digest('SHA-1', dataBuffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')
    return hashHex
  } catch (e) {
    console.log('error while encoding sha1', e)
    return data
  }
}

function isServerPages(options = {}) {
  let urlWithoutLocale = ''
  if (IS_BROWSER) {
    urlWithoutLocale = getUrlWithoutLocale({
      req: {
        originalUrl: window.location.pathname || '',
      },
    })
  } else {
    urlWithoutLocale = getUrlWithoutLocale(options)
  }
  return (
    urlWithoutLocale?.includes(pageNames.product) ||
    urlWithoutLocale?.includes(pageNames.catalog) ||
    urlWithoutLocale?.includes(pageNames.search) ||
    urlWithoutLocale == pageNames.home
  )
}
const constructHref = href => {
  const isLocalPath = href?.indexOf?.('//') < 0 && href?.indexOf?.('/') === 0
  const isNonHttpPath =
    href?.indexOf?.('http') < 0 && href?.indexOf?.('/') !== 0
  if (isLocalPath) {
    return `${location?.origin || ''}${href || ''}`
  } else if (isNonHttpPath) {
    const hrefSplit = href?.split('/')?.[0]
    if (hrefSplit?.indexOf('.') < 0) {
      return `${location?.origin || ''}${'/'}${href || ''}`
    } else {
      return href
    }
  } else {
    return href
  }
}

function parseMenuCategory(textToParse = '') {
  //Parsing for & - &amp;
  let parsedText = textToParse
    ?.replace(/(\&amp;|amp;)/g, '&')
    ?.replace(/&+/g, '&')
    ?.trim()

  //parse() - its parsing '' as [], so added a check to return '' if parsedText is ''
  return parsedText !== '' ? parse(parsedText) : ''
}

const getNormalizeText = text => {
  const normalizeText = text?.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  return normalizeText
}

const checkHomeMarket = customerData => {
  let homeCountry =
    customerData?.accounts?.properties?.find(
      ({ attributeId }) => attributeId === 'countryCode'
    )?.value ||
    storeContainer?.currentRegion ||
    ''
  const normalizedCountry = homeCountry === 'CA' ? 'Canada' : homeCountry
  return normalizedCountry === storeContainer?.storeIDValue
}
const getFirstName = text =>
  text?.length > 12 ? `${text?.slice(0, 11)}...` : text || ''

function enableBillingAddressRequired() {
  return convertToBoolean(APPConfig?.getAppConfig()?.isBillingAddressRequired)
}
export const checkMemberAgreementAndScroll = isMemberAgreementChecked => {
  if (!isMemberAgreementChecked) {
    const el = document.getElementById('clickwrap-container-2')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el.classList.add('highlight-ironclad')
      setTimeout(() => el.classList.remove('highlight-ironclad'), 2000)
    }
    return false
  }
  return true
}

const enableAccountSettings = () => {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableAccountSettings)
}
const enableProductOfferAdmin = () => {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableProductOfferV2)
}
const enablePlpRedesign = () => {
  return convertToBoolean(APPConfig?.getAppConfig()?.enablePlpRedesign)
}
const showKitStrikedPdpPriceForBA = () => {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.showKitStrikedPdpPriceForBA
  )
}
const enablePlpBannerImage = () => {
  return convertToBoolean(APPConfig?.getAppConfig()?.enablePlpBannerImage)
}
const enableAccountPrivacy = () => {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableAccountPrivacy)
}
const enableCustomizedOrderHistorySearchOption = () => {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.enableCustomizedOrderHistorySearchOption
  )
}

const enableAccountParticipantList = () => {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.enableAccountParticipantList
  )
}

const enablePdpRedesign = () => {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.enablePdpRedesign ?? 'true'
  )
}

const enablePersonalOfferPDP = () => {
  return convertToBoolean(APPConfig?.getAppConfig()?.enablePersonalOfferPDP)
}

const enableINLocalPhoneNumber = () => {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableINLocalPhoneNumber)
}

const enableSubProductsSubscribeSelect = () => {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.enableSubProductsSubscribeSelect
  )
}

const hideSubscriptionWidgetInPDP = () => {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.hideSubscriptionWidgetInPDP
  )
}

function getSkuInventoryStatus(sku, isBundleProduct = false) {
  const { productStatus = '' } = sku?.skuProperties || sku || {}

  let inventoryStatusView = ''
  let flagInventory = false
  let shouldDisableButton = false

  if (isBundleProduct && productStatus.toLowerCase() === 'bundleonly') {
    return {
      inventoryStatusView,
      flagInventory: false,
      shouldDisableButton: false,
    }
  }

  switch ((productStatus || '').toLowerCase()) {
    case 'discontinued':
      inventoryStatusView = i18nTranslate(
        'bundle.discontinuedV2Message',
        'Discontinued - This product or promotion has been discontinued',
        { nameSpace: 'ssr-resource' }
      )
      flagInventory = true
      shouldDisableButton = true
      break
    case 'replacement':
      inventoryStatusView = i18nTranslate(
        'bundle.replacement',
        'This is a replacement product and not available for purchase',
        { nameSpace: 'ssr-resource' }
      )
      flagInventory = true
      shouldDisableButton = true
      break
    case 'test':
      inventoryStatusView = 'This is a test sku'
      flagInventory = true
      shouldDisableButton = false
      break
    case 'preview':
      inventoryStatusView = i18nTranslate(
        'bundle.preview',
        'Preview product and will be launched soon',
        { nameSpace: 'ssr-resource' }
      )
      flagInventory = true
      shouldDisableButton = true
      break
    case 'bundleonly':
      inventoryStatusView = i18nTranslate(
        'pdp.bundleOnly',
        'Item not available for individual purchase',
        { nameSpace: 'ssr-resource' }
      )
      flagInventory = true
      shouldDisableButton = true
      break
    case 'inactive':
      inventoryStatusView = i18nTranslate(
        'item.notAvailable',
        'Item currently not available',
        { nameSpace: 'ssr-resource' }
      )
      flagInventory = true
      shouldDisableButton = true
      break
    default:
      // No status matched, do not show inventory message
      break
  }

  return { flagInventory, inventoryStatusView, shouldDisableButton }
}

function getSelectedSku(product, isOptionalProduct = false) {
  if (product.selectedSku) {
    return product.selectedSku
  }

  const defaultSkuId = product?.properties?.defaultSkuId || ''
  if (defaultSkuId && product.sku) {
    const skuByDefaultId = product.sku.find(
      sku => sku?.identifier === defaultSkuId
    )
    if (skuByDefaultId) return skuByDefaultId
  }

  if (!product.sku) return null

  if (isOptionalProduct) {
    return (
      product.sku.find(sku => sku.isOptionalSkuSelected) ||
      product.sku.find(sku => sku.default) ||
      product.sku[0] ||
      null
    )
  }

  return (
    product.sku.find(sku => sku.isSelectedForEdit) ||
    product.sku.find(sku => sku.default) ||
    product.sku[0] ||
    null
  )
}

function getNotPurchasableSkuStatus({
  normalSkuVariants = [],
  bundleMandatoryProducts = [],
  bundleOptionalProducts = [],
}) {
  // 1. Check normalSkuVariants
  let selectedSku =
    Array.isArray(normalSkuVariants) && normalSkuVariants?.length > 0
      ? normalSkuVariants?.find(sku => sku?.isSelected) ||
        normalSkuVariants?.find(sku => sku?.default) ||
        normalSkuVariants?.[0]
      : null

  if (selectedSku) {
    const status = getSkuInventoryStatus(selectedSku) || {}
    if (status?.flagInventory) return status
  }

  // 2. Check bundleMandatoryProducts
  for (const product of bundleMandatoryProducts) {
    const selectedMandatorySku = getSelectedSku(product)

    if (selectedMandatorySku) {
      const status = getSkuInventoryStatus(selectedMandatorySku, true) || {}
      if (status.flagInventory) return status
    }
  }

  // 3. Check bundleOptionalProducts (only if optionalProductAdded)
  for (const product of bundleOptionalProducts) {
    if (!product.optionalProductAdded || !Array.isArray(product.sku)) {
      continue
    }

    const selectedOptionalSku = getSelectedSku(product, true)

    if (selectedOptionalSku) {
      const status = getSkuInventoryStatus(selectedOptionalSku, true) || {}
      if (status.flagInventory) return status
    }
  }

  return {
    flagInventory: false,
    inventoryStatusView: '',
    shouldDisableButton: false,
  }
}

function getNotPurchasableStatusTestId(inventoryStatusView = '') {
  const status = inventoryStatusView?.toLowerCase() || ''
  if (status.includes?.('discontinued')) return 'product.discontinued'
  if (status.includes?.('replacement')) return 'product.replacement'
  if (status.includes?.('test sku')) return 'product.test'
  if (status.includes?.('preview')) return 'product.preview'
  if (status.includes?.('item not available for individual purchase'))
    return 'product.bundleOnly'
  if (status.includes?.('item currently not available'))
    return 'product.notAvailable'
  return 'product.inventoryStatus'
}

function enableJitsuSF() {
  try {
    return convertToBoolean(APPConfig?.getAppConfig()?.enableJITSUStorefront)
  } catch (error) {
    // Return false if APPConfig is not available (e.g., during tests)
    return false
  }
}

const isMysiteBAUserLoggedIn = response => {
  const { accounts = {}, guest } = response || {}
  const { accountType = '', type = '' } = accounts || {}
  const isBAUser =
    accountType?.includes?.('Brand Affiliate') ||
    type?.includes?.('Brand Affiliate')
  return isBAUser && !guest
}

const redirectToHomePage = () => {
  if (IS_BROWSER) {
    const curLocale = getLocaleCodeFromUrl({
      isReverseType: true,
      defaultLocale: 'en_US',
    })

    const [language, country] = curLocale?.includes?.('_')
      ? curLocale?.split?.('_')
      : curLocale?.split?.('-')

    return setTimeout(() => {
      window.location.href =
        `${window.location.origin}/${country}/${language}`.toLowerCase()
    }, 700)
  }
}

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isRetailToMember(accountsContainer, customerContainer) {
  return (
    accountsContainer?.convertAccoutType === 'Preferred Customer/Member' &&
    customerContainer?.profileResponse?.accounts?.accountType ===
      'Retail Customer'
  )
}

function getAccountTypes(accountsContainer, customerContainer) {
  const currentAccountType =
    customerContainer?.profileResponse?.accounts?.accountType || ''
  let convertingAccountType
  if (
    currentAccountType === 'Retail Customer' ||
    currentAccountType === 'Preferred Customer/Member'
  ) {
    convertingAccountType =
      accountsContainer?.convertAccoutType || 'Brand Affiliate'
  } else {
    convertingAccountType = 'Brand Affiliate - Business Entity'
  }
  return { currentAccountType, convertingAccountType }
}
function isRedesignMysiteUrl() {
  let isNewMysite = false
  const currentUrl = window.location.pathname
  if (IS_BROWSER && currentUrl && enableMysiteRedesignFeature()) {
    isNewMysite = currentUrl.includes('/mysite/') || false
  }
  return isNewMysite
}

function isRedesignPOUrl() {
  let isNewPO = false
  const currentUrl = window.location.pathname
  if (currentUrl && enablePORedesignFeature()) {
    isNewPO = currentUrl.includes('/personal-offer/') || false
  }
  return isNewPO
}

function isStateValidationSkippedForCountry(locale) {
  const skipStateValidationValue =
    storeContainer?.globalStoreResponse?.properties?.find(
      prop => prop?.name === 'skipStateValidation'
    )?.value

  const DEFAULT_SKIP_STATE_COUNTRIES = [
    'NZ',
    'ZA',
    'AT',
    'BE',
    'CZ',
    'DK',
    'FI',
    'FR',
    'DE',
    'HU',
    'IS',
    'IE',
    'IT',
    'LU',
    'NL',
    'NO',
    'PL',
    'PT',
    'RO',
    'SK',
    'ES',
    'SE',
    'CH',
    'GB',
    'NC',
    'PF',
    'SG',
    'PH',
  ]

  const country =
    String(locale || '')
      .split('_')[1]
      ?.toUpperCase() || ''

  try {
    if (skipStateValidationValue) {
      const parsed = JSON.parse(skipStateValidationValue)
      if (Array.isArray(parsed)) {
        return parsed.includes(country)
      }
    }
  } catch (e) {
    console.error(
      'isStateValidationSkippedForCountry: failed to parse skipStateValidation value',
      e
    )
  }

  return DEFAULT_SKIP_STATE_COUNTRIES.includes(country)
}

function isPatchAddressAfterCartSuccess() {
  return (
    convertToBoolean(
      APPConfig.getAppConfig?.()?.patchAddressAfterCartSuccess
    ) || false
  )
}

function isInitBraintreePayment() {
  return convertToBoolean(APPConfig.getAppConfig?.()?.initBraintreePayment)
}

function getDefaultPaymentMethod() {
  return APPConfig.getAppConfig()?.defaultPaymentMethod?.toLowerCase() || ''
}

function restrictOtherMarketUser(currentPageUrl, customerContainer) {
  let show = false
  const currentPageURl = currentPageUrl
  const isAuthenticated = getCookie('x-role-user') ? true : false
  const homeCountry = customerContainer?.getUserHomeCountry() || ''
  const pageURl = window.location.href || ''
  const accountType = getLocalStorage('accountType') || ''
  const curLocale = getLocaleCodeFromUrl({
    url: pageURl,
    at: 'pathParam',
    defaultLocale: APPConfig.getAppConfig().defaultLocale || 'en_US',
    isReverseType: true,
  })
  const [language, activeCountryCode] = curLocale.includes('_')
    ? curLocale.split('_')
    : curLocale.split('-')

  const isRestrictCrossMarketAccess = convertToBoolean(
    storeContainer?.globalStoreResponse?.properties?.find(
      prop => prop?.name === 'enableRestrictedUserAccess'
    )?.value || 'false'
  )
  let isNativeApp = IS_BROWSER ? window?.isNativeApp || false : false
  const isCrossMarketAllowedFlag =
    customerContainer.isCrossMarketAllowed() || false
  if (
    isAuthenticated === true &&
    homeCountry !== activeCountryCode &&
    currentPageURl !== '/logout/callback' &&
    currentPageURl !== '/login/callback' &&
    accountType === 'Retail Customer' &&
    isNativeApp === false &&
    homeCountry !== '' &&
    !isCrossMarketAllowedFlag
  ) {
    show = true
  } else if (
    isRestrictCrossMarketAccess &&
    homeCountry !== '' &&
    customerContainer.restrictCrossMarketAccess(homeCountry, activeCountryCode)
  ) {
    show = true
  }

  return show
}

function isPreviewMode(pageUrl) {
  const urlParams = new URLSearchParams(new URL(pageUrl).search)
  const hasPreviewParam = urlParams?.has('preview')
  const { isMysiteRedesignFeatureEnabled = false } =
    APPConfig?.getAppConfig() || {}
  const isMysiteRedesign = convertToBoolean(isMysiteRedesignFeatureEnabled)
  const isEqMysiteUrl = pageUrl?.includes('/mysite')
  const isMysitePreview =
    (isEqMysiteUrl && isMysiteRedesign && hasPreviewParam) || false
  const isEqPOUrl = pageUrl?.includes('/personal-offer/')
  const isPOPreview =
    (isEqPOUrl && enablePORedesignFeature() && hasPreviewParam) || false
  const isPreviewPage = isMysitePreview || isPOPreview
  return isPreviewPage
}

function isINMarket() {
  const locale = getLocaleCodeFromUrl({
    defaultLocale: 'en_US',
    isReverseType: true,
  })
  const country = locale?.split('_')[1]
  return country === 'IN'
}

function showCrossSellProducts() {
  return convertToBoolean(APPConfig.getAppConfig().crossSellProductsInPDP)
}

function isFeatureEnabled(configKey) {
  return convertToBoolean(APPConfig?.getAppConfig()?.[configKey])
}

function getTrackingPrefix() {
  const isPersonalOffer = isRedesignPOUrl()
  const isMysiteUrl = isRedesignMysiteUrl()
  const mysitePrefix = isMysiteUrl ? 'mysite_' : ''
  return isPersonalOffer ? '' : mysitePrefix
}

function checkForLegacyURL(list) {
  return list?.map(val => {
    const link = val?.linkInfo?.link
    if (link && (link.includes('/content') || link.includes('/site'))) {
      return { ...val, linkInfo: { ...val.linkInfo, isThirdPartyPage: true } }
    }
    return val
  })
}

function getAbsoluteURLForLegacy(isLegacyURL, href) {
  if (
    isLegacyURL &&
    !href.includes('https') &&
    !href.includes('http') &&
    window
  ) {
    const marketRegion = getLocalePathParam()
    const currentDomainName = window?.location?.origin?.split('//')[1]
    if (!href.includes(currentDomainName)) {
      if (href.includes('/site/')) {
        href = '//' + currentDomainName + '/' + marketRegion + href
        const duplicate = `${marketRegion}/${marketRegion}`
        if (href?.includes(duplicate)) {
          href = href?.replace(duplicate, marketRegion)
        }
      } else {
        href = '//' + currentDomainName + href
      }
    }
    if (!href.includes('https') && !href.includes('http')) {
      href = 'https:' + href
    }
  }
  return href
}

/**
 * Absolute URLs must render as plain anchors: a react-router <Link> prepends
 * the locale basename and produces broken URLs like
 * /th/en/http://www.seachildrenheartfund.org/
 */
function isExternalUrl(href) {
  return (
    typeof href === 'string' &&
    (/^https?:\/\//i.test(href) ||
      href.startsWith('//') ||
      href.startsWith('www.'))
  )
}

function normalizeExternalUrl(href) {
  return typeof href === 'string' && href.startsWith('www.')
    ? `https://${href}`
    : href
}

if (IS_BROWSER) {
  window.trackErrorInInstana = trackErrorInInstana
}

function getGPSConfig(configKey) {
  return convertToBoolean(APPConfig?.getAppConfig()?.gpsConfig?.[configKey])
}
function enableCartMFE() {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableCartMFE)
}

function enableMyAccountsMFE() {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableMyAccountsMFE)
}

// Routes the whole /myaccount subtree into the account-profile full-shell MFE
// (sidebar + content). Config-gated to LATAM/PAC; US/CA keep the local shell.
function enableAccountProfileMFE() {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableAccountProfileMFE)
}

function isAccountProfileDelegatedRoute(pathname = '') {
  const routes = APPConfig?.getAppConfig()?.accountProfileDelegatedRoutes || []
  return routes?.some(segment => {
    const base = `/myaccount/${segment}`
    // endsWith handles exact + locale-prefixed (/en/us/myaccount/favorites)
    // includes(base + '/') handles sub-paths without false-positives (/myaccount/favoritesmanager)
    return pathname.endsWith(base) || pathname.includes(`${base}/`)
  })
}

function getNestedConfigValue(propertyName, configKey) {
  return APPConfig?.getAppConfig()?.[propertyName]?.[configKey] || ''
}

function enableHomePageSSR() {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableHomePageSSR)
}

/**
 * Gets the default SKU from a parsed product.
 *
 * @param {Object} parsedProduct - The parsed product object
 * @returns {Object|null} The default SKU or the first SKU if no default is set, null if no SKUs exist
 */
function getDefaultSku(parsedProduct) {
  if (
    !parsedProduct?.sku ||
    !Array.isArray(parsedProduct?.sku) ||
    parsedProduct?.sku?.length === 0
  ) {
    return null
  }
  return (
    parsedProduct.sku.find(sku => sku?.default === true) ||
    parsedProduct?.sku?.[0]
  )
}

/**
 * Checks if a product should be disabled based on its status and inventory.
 *
 * @param {Object} parsedProduct - The parsed product object
 * @returns {boolean} True if product should be disabled, false otherwise
 */
function isProductStatusDisabled(parsedProduct = null) {
  if (!parsedProduct) return true

  const normalizeString = str =>
    String(str || '')
      .toLowerCase()
      .replace(/\s+/g, '')
      .trim()

  const defaultSku = getDefaultSku(parsedProduct)

  const productStatus = normalizeString(
    defaultSku?.properties?.productStatus ||
      defaultSku?.properties?.status ||
      parsedProduct?.properties?.productStatus ||
      parsedProduct?.properties?.status
  )
  const inventory = normalizeString(
    defaultSku?.inventory ||
      defaultSku?.properties?.inventoryStatus ||
      parsedProduct?.properties?.inventoryStatus
  )

  const disabledStatuses = [
    'replacement',
    'previewproduct',
    'preview',
    'stopped',
    'bundleonly',
    'discontinued',
    'dangerousgoods',
    'notavailable',
    'inactive',
  ]

  const disabledInventoryStatuses = ['outofstock', 'unavailable']

  // For bundles (may have no sku array), always enable unless permanently non-sellable
  // (sub-item inventory issues are temporary, handled on landing page)
  if (parsedProduct?.type?.toLowerCase() === 'bundle') {
    return disabledStatuses.includes(productStatus)
  }

  // For inventory: only disable if ALL SKUs are OOS.
  // If at least one variant is in stock, allow the user to add the product.
  const skus = Array.isArray(parsedProduct?.sku) ? parsedProduct.sku : []

  if (productStatus === 'stopped') return true

  // For multi-variant products whose default SKU has a disabled status: check if
  // any other SKU has a non-disabled productStatus. If yes, the product is still
  // selectable (the user can choose that variant on landing page).
  // Only disable if ALL variants are permanently non-sellable (inactive/disc/stopped).
  if (skus.length > 1 && disabledStatuses.includes(productStatus)) {
    const hasSellableVariant = skus.some(sku => {
      const skuStatus = normalizeString(
        sku?.properties?.productStatus || sku?.properties?.status
      )
      return !disabledStatuses.includes(skuStatus)
    })
    // Disable only if ALL variants are permanently non-sellable
    if (!hasSellableVariant) return true
    // Has at least one sellable variant — enable the product
    // (inventory status is temporary, allow BA to add to PO)
    return false
  }

  if (disabledStatuses.includes(productStatus)) return true

  // For multi-variant products with active status, always enable
  // (allows BA to add to PO even if all variants are temporarily OOS)
  if (skus.length > 1) {
    return false
  }

  // For single products, disable only if OOS
  return disabledInventoryStatuses.includes(inventory)
}

function getMaxLength(inputType, defaultValue = 30) {
  const addressMaxLengthInputs =
    APPConfig?.getAppConfig()?.addressMaxLengthInputs || {}
  return addressMaxLengthInputs[inputType] || defaultValue
}

function validateMaxLengthForAddressLine(address) {
  const showAddressLineMaxLengthInfo = isFeatureEnabled(
    'enableAddressLineMaxLengthInfoMessage'
  )
  const addressLine1MaxLength = getMaxLength('addressLine1', 255)
  const addressLine2MaxLength = getMaxLength('addressLine2', 255)
  if (
    showAddressLineMaxLengthInfo &&
    (address?.addressLine1?.length > addressLine1MaxLength ||
      address?.addressLine2?.length > addressLine2MaxLength)
  ) {
    return true
  }
  return false
}

function enableHeaderMFE() {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableHeaderMFE)
}

function enableHeaderMFESSR() {
  return convertToBoolean(APPConfig?.getAppConfig()?.enableHeaderMFESSR)
}

function extractSegmentAfterPattern(url, pattern) {
  return (
    url?.split(pattern)?.[1]?.split('/')?.[0]?.split('?')?.[0]?.trim() || ''
  )
}

function getBundleThumbnail(item) {
  const bundleSku = item?.skus?.find(
    sku => sku?.type?.toUpperCase() === PRODUCT_TYPE.BUNDLE
  )
  if (bundleSku?.skuProperties?.productImages) {
    try {
      const images = JSON.parse(bundleSku.skuProperties.productImages)
      return images?.[0]?.thumbnail || ''
    } catch (e) {
      console.error('Failed to parse productImages:', e)
    }
  }
  return ''
}

function stripCurrencySymbol(price = '') {
  const priceStr = String(price || '')
  if (!priceStr) {
    return ''
  }
  try {
    const { defaultLocale = '', normalizeCurrencyLocale = {} } =
      APPConfig.getAppConfig() || {}
    let locale = defaultLocale.replace('_', '-')?.replace(/[^a-zA-Z0-9-]/g, '')
    const localeCountry = locale.split('-')[1]
    if (locale.includes('-US')) {
      locale = 'en-US'
    } else if (locale.includes('-CA')) {
      locale = 'en-CA'
    } else if (
      localeCountry &&
      normalizeCurrencyLocale[localeCountry.toLowerCase()]
    ) {
      const languageCode = normalizeCurrencyLocale[localeCountry.toLowerCase()]
      locale = languageCode + '-' + localeCountry
    }
    const currencyCode = getCurrency(locale)
    const formatOptions = {
      style: 'currency',
      currency: currencyCode,
    }
    if (enableNewFeature('enableNarrowSymbolCurrencyFormat')) {
      formatOptions.currencyDisplay = 'narrowSymbol'
    }
    const currencySymbol = new Intl.NumberFormat(
      locale || undefined,
      formatOptions
    )
      .formatToParts(0)
      .find(part => part.type === 'currency')?.value

    if (!currencySymbol) {
      return priceStr
    }
    const escapedCurrencySymbol = currencySymbol.replace(
      /[.*+?^${}()|[\]\\]/g,
      String.raw`\$&`
    )
    return priceStr.replace(new RegExp(escapedCurrencySymbol, 'g'), '').trim()
  } catch (e) {
    // Fallback for malformed locale/currency or environments with limited Intl support.
    console.error(
      'stripCurrencySymbol: failed to resolve currency symbol via Intl',
      e
    )
    return priceStr
      .replace('$', '')
      .replace('S/', '')
      .replace('NZ', '')
      .replace('JPY', '')
      .replace('JP¥', '')
      .replace('\u00A5', '')
      .replace('\uFFE5', '')
      .trim()
  }
}

/**
 * Account type hierarchy levels (higher number = higher tier)
 * Used to determine if conversion should be blocked (same type or downgrade)
 */
const ACCOUNT_TYPE_HIERARCHY = {
  /** Brand Affiliate - highest tier */
  'Brand Affiliate': 3,
  'Brand Affiliate - Business Entity': 3,
  BA: 3,
  BRAND_AFFILIATE: 3,
  /** Preferred Customer/Member - middle tier */
  'Preferred Customer/Member': 2,
  MEMBER: 2,
  member: 2,
  /** Retail Customer - lowest tier */
  'Retail Customer': 1,
  RETAIL: 1,
  retail: 1,
}

/**
 * Account type labels for display purposes
 * Maps various account type keys to user-friendly display labels using translations
 * @param {string} type - Account type key
 * @returns {string} - Translated display label
 */
function getAccountTypeLabelText(type) {
  const labelMap = {
    BA: () => i18nTranslate('signup.brandAffiliate', 'Brand Affiliate'),
    ba: () => i18nTranslate('signup.brandAffiliate', 'Brand Affiliate'),
    BRAND_AFFILIATE: () =>
      i18nTranslate('signup.brandAffiliate', 'Brand Affiliate'),
    brand_affiliate: () =>
      i18nTranslate('signup.brandAffiliate', 'Brand Affiliate'),
    'Brand Affiliate': () =>
      i18nTranslate('signup.brandAffiliate', 'Brand Affiliate'),
    'Brand Affiliate - Business Entity': () =>
      i18nTranslate('signup.brandAffiliate', 'Brand Affiliate'),
    MEMBER: () => i18nTranslate('accountTypeLabel.member', 'Member'),
    member: () => i18nTranslate('accountTypeLabel.member', 'Member'),
    'Preferred Customer/Member': () =>
      i18nTranslate('accountTypeLabel.member', 'Member'),
    RETAIL: () => i18nTranslate('accountTypeLabel.retail', 'Retail'),
    retail: () => i18nTranslate('accountTypeLabel.retail', 'Retail'),
    'Retail Customer': () => i18nTranslate('accountTypeLabel.retail', 'Retail'),
  }
  return labelMap[type]?.() || type
}

/**
 * Lookup map for normalizing invite type parameters
 */
const INVITE_TYPE_MAP = {
  BA: 'Brand Affiliate',
  BRAND_AFFILIATE: 'Brand Affiliate',
  MEMBER: 'Preferred Customer/Member',
  PREFERRED_CUSTOMER: 'Preferred Customer/Member',
  RETAIL: 'Retail Customer',
  RETAIL_CUSTOMER: 'Retail Customer',
}

/**
 * Normalizes invite type parameter to match hierarchy keys
 * @param {string} type - The invite type to normalize
 * @returns {string} - Normalized account type string
 */
function normalizeInviteType(type) {
  if (!type) return ''
  return INVITE_TYPE_MAP[type.toUpperCase()] || type
}

/**
 * Checks if the current page is the Product Offer Admin screen.
 *
 * @returns {boolean} True if the current pathname includes the Product Offer Admin page, false otherwise
 */
function isPOAdminScreen() {
  if (typeof window !== 'undefined') {
    return window.location.pathname?.includes(pageNames.productOfferAdmin)
  }
  return false
}

function isCSRActOnBehalfSession() {
  if (!IS_BROWSER) {
    return false
  }

  const csrAdminName = getLocalStorage('csrAdminName') || ''
  return Boolean(csrAdminName)
}

function escapeHtml(str) {
  return str
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function convertMarkdownToHtml(text) {
  if (!text || typeof text !== 'string') return ''

  let htmlText = escapeHtml(text)

  // Convert bold syntax: **text** to <strong>text</strong>
  htmlText = htmlText.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

  htmlText = htmlText.replace(/\\([\\`*_{}[\]()#+\-.!])/g, '$1')

  return htmlText
}

function is3DSEnabledForGPS(validProcessorObj) {
  if (!IS_BROWSER) {
    return false
  }

  return isPaymetric3DSProcessor(validProcessorObj)
}

async function deletePaymentById({ paymentId, fetchResponse }) {
  if (!paymentId) {
    return
  }

  await fetchResponse({
    endPointName: 'deletePayment',
    pathParams: `${paymentId}`,
  })
}

// Query param names read/written by handleGPS3DSRedirectStatus - centralized so
// a future param rename only needs to happen here.
const GPS_PARAM = {
  STATUS: 'status',
  GPS_STATUS: 'gps_status',
  ORDER_ID: 'orderId',
}

// Only a response with a `code` is an actual API error (vs a thrown JS/network
// error) - its `message` is shown to the user as-is, straight from the backend.
function getGpsZeroDollarAuthErrorMessage(response) {
  return response?.code ? response?.message || '' : null
}

// Extracted only to keep handleGPS3DSRedirectStatus under the cognitive
// complexity limit - absorbs the extra branching added for error reporting.
// Returns the zeroDollarAuth failure response/error (or null on success),
// after deleting the payment on any non-approved status or failure.
async function performGpsZeroDollarAuth({
  gpsStatus,
  resolvedPaymentId,
  gps3dsOrderId,
  fetchResponse,
  isSuccessResponse,
  deleteResolvedPayment,
  storeId,
  zeroDollarAuthEndPointName,
}) {
  if (gpsStatus !== 'approved' || !resolvedPaymentId || !gps3dsOrderId) {
    await deleteResolvedPayment()
    return null
  }

  try {
    const zeroDollarAuthResponse = await fetchResponse({
      endPointName: zeroDollarAuthEndPointName,
      pathParams: `${resolvedPaymentId}/zeroDollarAuth`,
      queryParams: {
        storeId,
        threeDsEnabled: true,
        gps3dsOrderId,
      },
      postData: {},
    })

    if (!isSuccessResponse(zeroDollarAuthResponse)) {
      await deleteResolvedPayment()
      return zeroDollarAuthResponse
    }
    return null
  } catch (error) {
    await deleteResolvedPayment()
    return error
  }
}

async function handleGPS3DSRedirectStatus({
  paymentIdSessionKey,
  orderIdSessionKey,
  deletePaymentById,
  fetchResponse,
  isSuccessResponse,
  storeId,
  zeroDollarAuthEndPointName = 'addAccountPaymentForPaymetric3ds',
}) {
  if (!IS_BROWSER) {
    return
  }

  const currentLocation = window?.location
  if (!currentLocation) {
    return
  }

  const currentHref = currentLocation.href || ''
  const urlParams = new URLSearchParams(currentLocation.search || '')
  let gpsStatus = (
    urlParams.get(GPS_PARAM.STATUS) ||
    urlParams.get(GPS_PARAM.GPS_STATUS) ||
    ''
  ).toLowerCase()

  if (!gpsStatus) {
    const gpsStatusMatch = new RegExp(
      `[?&](?:${GPS_PARAM.STATUS}|${GPS_PARAM.GPS_STATUS})=([^&#]+)`,
      'i'
    ).exec(currentHref)
    gpsStatus = (gpsStatusMatch?.[1] || '').toLowerCase()
  }

  if (!gpsStatus) {
    return
  }

  const paymentId = sessionStorage.getItem(paymentIdSessionKey) || ''
  let gps3dsOrderId =
    urlParams.get(GPS_PARAM.ORDER_ID) ||
    sessionStorage.getItem(orderIdSessionKey) ||
    ''

  if (!gps3dsOrderId) {
    const orderIdMatch = new RegExp(
      `[?&]${GPS_PARAM.ORDER_ID}=([^&#?]+)`,
      'i'
    ).exec(currentHref)
    gps3dsOrderId = orderIdMatch?.[1] || ''
  }

  const resolvedPaymentId =
    paymentId ||
    (typeof gps3dsOrderId === 'string' ? gps3dsOrderId.split('_')?.[0] : '')

  const deleteResolvedPayment = async () => {
    if (resolvedPaymentId) {
      await deletePaymentById(resolvedPaymentId)
    }
  }

  // Surfaced to the caller so it can show an error toast once cleanup (below) finishes.
  let zeroDollarAuthError
  try {
    zeroDollarAuthError = await performGpsZeroDollarAuth({
      gpsStatus,
      resolvedPaymentId,
      gps3dsOrderId,
      fetchResponse,
      isSuccessResponse,
      deleteResolvedPayment,
      storeId,
      zeroDollarAuthEndPointName,
    })
  } finally {
    sessionStorage.removeItem(paymentIdSessionKey)
    sessionStorage.removeItem(orderIdSessionKey)

    // Clear only the GPS-added params (status/gps_status/orderId) in place via
    // history.replaceState, preserving any other query params (e.g. ?id=) that
    // were already part of the landing page URL before the GPS redirect.
    const cleanPathname = (currentLocation.pathname || '')
      .split('&')?.[0]
      .split('?')?.[0]
    urlParams.delete(GPS_PARAM.STATUS)
    urlParams.delete(GPS_PARAM.GPS_STATUS)
    urlParams.delete(GPS_PARAM.ORDER_ID)
    const remainingQuery = urlParams.toString()
    const sanitizedPath = remainingQuery
      ? `${cleanPathname}?${remainingQuery}`
      : cleanPathname

    try {
      // A throw here must not escape this finally and abort the caller's await.
      window.history.replaceState(window.history.state, '', sanitizedPath)
    } catch (urlCleanupError) {
      console.warn('GPS 3DS URL cleanup failed', urlCleanupError)
    }
  }

  if (zeroDollarAuthError) {
    const message = getGpsZeroDollarAuthErrorMessage(zeroDollarAuthError)
    if (message) {
      return { hasError: true, message }
    }
    return
  }

  // Lets the caller point the owning record (e.g. a subscription) at the card
  // now that 3DS has validated it.
  if (gpsStatus === 'approved' && resolvedPaymentId) {
    return { isApproved: true, paymentId: resolvedPaymentId }
  }
}

async function triggerGPS3DSInitiateCall({
  paymentId,
  isGPS3DSEnabled,
  isCSRActOnBehalf,
  deletePaymentById,
  fetchResponse,
  isSuccessResponse,
  storeId,
  paymentIdSessionKey,
  orderIdSessionKey,
  initiateEndPointName = 'addAccountPaymentForPaymetric3ds',
}) {
  if (!IS_BROWSER || !isGPS3DSEnabled || !paymentId || isCSRActOnBehalf) {
    return null
  }

  const loadParams = {
    endPointName: initiateEndPointName,
    pathParams: `${paymentId}/gps3dsInitiate`,
    queryParams: {
      storeId,
    },
    postData: {
      landingUrl: (() => {
        const currentHref = window?.location?.href || ''
        return currentHref.includes('?') ? currentHref : `${currentHref}?`
      })(),
    },
  }

  try {
    const response = await fetchResponse(loadParams)
    if (isSuccessResponse(response) && response?.redirectUrl) {
      const gps3dsOrderId =
        response?.redirectUrl?.split('redirect/')?.[1]?.split('?')?.[0] || ''

      sessionStorage.setItem(paymentIdSessionKey, `${paymentId || ''}`)
      sessionStorage.setItem(orderIdSessionKey, gps3dsOrderId)
      window.location.replace(response.redirectUrl)
      return response
    }

    await deletePaymentById(paymentId)
    return response
  } catch (error) {
    await deletePaymentById(paymentId)
    return error
  }
}

const FOCUS_RESTORE_STORAGE_KEY = 'focusOn'
const FOCUS_RESTORE_MAX_AGE_MS = 5000
let isTrackingFocusin = false

/**
 * Remembers `elementId` in sessionStorage so focus can be restored to it
 * later via restoreFocusAfterRemount, once the element reappears in the DOM.
 */
function rememberFocusTarget(elementId) {
  if (elementId) {
    setSessionStorage(FOCUS_RESTORE_STORAGE_KEY, {
      selector: `#${elementId}`,
      timestamp: Date.now(),
    })
  }
}

/**
 * Starts a single, page-lifetime `focusin` listener (safe to call from every
 * mount of every caller) that remembers whichever focused element matches
 * `selector`. Only attaches once so remounts don't stack up duplicate
 * listeners that can race and clobber a different caller's stored target.
 * Note: only the first caller's `selector` is ever used (by design, to keep
 * a single listener) - if a second caller needs a different selector, this
 * will need to support multiple selectors.
 */
function trackFocusWithin(selector) {
  if (!IS_BROWSER || isTrackingFocusin) return
  isTrackingFocusin = true
  document.addEventListener('focusin', event => {
    const el = event.target.closest(selector)
    if (el?.id) {
      rememberFocusTarget(el.id)
    }
  })
}

/**
 * Restores focus to the element remembered via rememberFocusTarget, if any.
 * Returns the setTimeout id so callers can cancel it on unmount (avoids a
 * stale, unmounted caller's timer firing later and clobbering a different
 * caller's stored target).
 */
function restoreFocusAfterRemount(delay = 100) {
  if (!IS_BROWSER) return null
  return setTimeout(() => {
    const stored = getSessionStorage(FOCUS_RESTORE_STORAGE_KEY)
    if (!stored?.selector) return
    // dropped instead of restored - an old, never-consumed target shouldn't
    // steal focus on some unrelated later mount/update
    if (Date.now() - stored.timestamp > FOCUS_RESTORE_MAX_AGE_MS) {
      deleteFromSessionStorage(FOCUS_RESTORE_STORAGE_KEY)
      return
    }
    const el = document.querySelector(stored.selector)
    if (el) {
      el.focus()
      deleteFromSessionStorage(FOCUS_RESTORE_STORAGE_KEY)
    }
  }, delay)
}

export {
  DEFAULT_AFFILIATION,
  DEFAULT_BRAND,
  isRetailToMember,
  isObject,
  checkHomeMarket,
  getNormalizeText,
  currencyFormat,
  volumeFormat,
  deformatCurrency,
  convertToBoolean,
  observeElement,
  getCurrency,
  getCurrencySymbol,
  getLocaleString,
  isValidUrl,
  setResponsiveValues,
  tryJSONParse,
  tryDecode,
  encodeSearchTerm,
  decodeSearchTerm,
  safeParseInt,
  getLocalePathParam,
  renderLinkCheck,
  getMySiteUrl,
  checkEnableButton,
  isUrlAbsolute,
  getBrowserIpAddress,
  replaceLocaleInURL,
  getCountryCode,
  getImageFromProperty,
  isPageFound,
  getMysiteCartUrl,
  getMySitePriceFlag,
  checkMysiteOrPersonalOffer,
  getNamefromFirstname,
  getBynderImage,
  getBynderAltText,
  getProductPDPTitle,
  getMedia,
  trackErrorInInstana,
  filterWhiteLabelEmails,
  getFooterBynderImage,
  getBynderAltTextSrc,
  convertTime,
  setMysiteOrPersonalOffer,
  getShoppingContext,
  getShoppingContextPrysm,
  clearShoppingContext,
  checkCustomerTypeEligibility,
  capitalizeFirstLetter,
  getRedirectUrl,
  getTotalPagesForPagination,
  getInventoryMessage,
  isBundleHasDiscontinuedSubItem,
  modifyAndGetCustomerType,
  modifyAndGetAccountType,
  getBundleReorderInfo,
  getFraudDetectionCbkStatus,
  setFraudDetectionCbkStatus,
  checkProductBackOrdered,
  getOrderHistoryBackOrderedDate,
  isGPSEnabled,
  getParsedPhoneNumber,
  displayOrderReturnForSupportedLocales,
  emailValidation,
  getLiveEventStatus,
  getFavoritesEnabled,
  getWebAvailableChannel,
  enablePhoneVerification,
  enablePhoneVerificationForConversion,
  enablePhoneVerificationOnLanding,
  enablePhoneVerificationInProfileUpdate,
  enableLegalAddress,
  enableLegalAddressForMemConversion,
  enableFeatureFlagtoApi,
  enableCSErrorCodeMessage,
  getInputFormJSON,
  getShouldEnableProfileEditInEvent,
  isNonEventOrEventWithSub,
  jitsuSignUpRedirect,
  signUpRedirectionForDirectSellerPopUp,
  signUp,
  account,
  checkForSkusArrayInfo,
  getCookiesFeatureOverride,
  getEventNotificationTimeline,
  getCurrentLiveEventTimelineKey,
  isLiveEventActionDisabledByKey,
  shouldDisableLiveEventCartAction,
  enableJITSU,
  setStoragesForAccountConversion,
  isGuestCheckoutEnabledForWeb,
  getGPSConfigValue,
  isToEnableEQGuestFastFlow,
  getSponsorInfo,
  setUTMInfo,
  getUTMInfo,
  getUTTInfo,
  stripOffExtraDoubleQuotes,
  toAmount,
  toQuantity,
  getFrequencyLabel,
  getItemVariant,
  getItemCouponCode,
  resolveItemBrand,
  getGuestSponsorId,
  getGuestSignupOrigin,
  encodeSHA1,
  isExpressCheckout,
  isExpressCart,
  delay,
  isServerPages,
  getCheckoutGTMPageData,
  constructHref,
  notAllowDangerousGoodsToCart,
  enableMultipleSubscription,
  parseMenuCategory,
  getFirstName,
  enableBillingAddressRequired,
  enableAccountSettings,
  enableProductOfferAdmin,
  enablePlpRedesign,
  showKitStrikedPdpPriceForBA,
  enablePlpBannerImage,
  getSkuInventoryStatus,
  getNotPurchasableSkuStatus,
  getNotPurchasableStatusTestId,
  enableJitsuSF,
  redirectToHomePage,
  isMysiteBAUserLoggedIn,
  enableAccountPrivacy,
  enableAccountParticipantList,
  enablePdpRedesign,
  enablePersonalOfferPDP,
  enableSubProductsSubscribeSelect,
  hideSubscriptionWidgetInPDP,
  enableLegalTaxIdForMemConversion,
  enableMysiteRedesignFeature,
  getAccountTypes,
  enableNewFeature,
  isEligibleToShowPVCVSV,
  getIsDisablePVCVSVPriceForAccount,
  disableLegalMarketCheckboxAccConv,
  isRedesignMysiteUrl,
  isPatchAddressAfterCartSuccess,
  isInitBraintreePayment,
  enablePORedesignFeature,
  hasProductOfferPercentageDiscount,
  enableSafeRedirectHardening,
  showPrysmBadge,
  isRedesignPOUrl,
  getDefaultPaymentMethod,
  restrictOtherMarketUser,
  getGPSConfig,
  isPreviewMode,
  isDobEnabled,
  isDobMandatoryForConversion,
  isDobOptionalForConversion,
  isINMarket,
  showCrossSellProducts,
  enableCustomizedOrderHistorySearchOption,
  getTrackingPrefix,
  isFeatureEnabled,
  getAbsoluteURLForLegacy,
  checkForLegacyURL,
  isExternalUrl,
  normalizeExternalUrl,
  getNestedConfigValue,
  enableCartMFE,
  enableMyAccountsMFE,
  enableAccountProfileMFE,
  isAccountProfileDelegatedRoute,
  enableHomePageSSR,
  getDefaultSku,
  isProductStatusDisabled,
  validateMaxLengthForAddressLine,
  enableHeaderMFE,
  enableHeaderMFESSR,
  getMaxLength,
  extractSegmentAfterPattern,
  enableINLocalPhoneNumber,
  getBundleThumbnail,
  stripCurrencySymbol,
  ACCOUNT_TYPE_HIERARCHY,
  getAccountTypeLabelText,
  normalizeInviteType,
  isPOAdminScreen,
  hideRegionalTaxLabel,
  isStateValidationSkippedForCountry,
  isCSRActOnBehalfSession,
  is3DSEnabledForGPS,
  deletePaymentById,
  handleGPS3DSRedirectStatus,
  triggerGPS3DSInitiateCall,
  rememberFocusTarget,
  trackFocusWithin,
  restoreFocusAfterRemount,
  convertMarkdownToHtml,
}
